import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, apiErr } from "../lib/api";
import { money, FREQ_LABELS, fmtDateTime } from "../lib/format";
import { toast } from "sonner";
import { PlayCircle, Calculator, CheckCircle2, Info, Eye, AlertTriangle, FileText, Save, X, RotateCcw, UserRound } from "lucide-react";
import CompanyBreadcrumb from "../components/CompanyBreadcrumb";
import { useClientPerms } from "../lib/useClientPerms";
import { useAuth } from "../context/AuthContext";
import { DraftActivity } from "../components/DraftActivity";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";

export default function RunPayroll() {
  const location = useLocation();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [employees, setEmployees] = useState([]);
  const [hours, setHours] = useState({});
  const [payouts, setPayouts] = useState({});
  const [scheduleName, setScheduleName] = useState("");
  const [nextInfo, setNextInfo] = useState(null);
  const [applyHoliday, setApplyHoliday] = useState(true);
  const [holidayOverrides, setHolidayOverrides] = useState({});
  const [otOverrides, setOtOverrides] = useState({});
  const [otChoice, setOtChoice] = useState({});
  const [payoutAmounts, setPayoutAmounts] = useState({});
  const [payoutReasons, setPayoutReasons] = useState({});
  const [payMethods, setPayMethods] = useState({});
  const [holidayChoice, setHolidayChoice] = useState({});
  const [vacOverrides, setVacOverrides] = useState({});
  const [vacReasons, setVacReasons] = useState({});
  const [lineOverrides, setLineOverrides] = useState({});
  const [excluded, setExcluded] = useState([]);
  const [runSource, setRunSource] = useState("manual");
  const [offCycle, setOffCycle] = useState(false);
  const [offCycleType, setOffCycleType] = useState("bonus");
  const [sourceReason, setSourceReason] = useState("");
  const [showSourceChange, setShowSourceChange] = useState(false);
  const [timeBreakdown, setTimeBreakdown] = useState({});
  const [supersedesRunId, setSupersedesRunId] = useState(null);
  const [meta, setMeta] = useState(() => {
    const today = new Date().toISOString().slice(0, 10);
    return { pay_period_start: today, pay_period_end: today, pay_date: today, pay_frequency: "biweekly" };
  });
  const [preview, setPreview] = useState(null);
  const [detail, setDetail] = useState(null);
  const [adjustments, setAdjustments] = useState({});
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [draftMeta, setDraftMeta] = useState(null);          // {saved_by, saved_by_name, saved_at, version} from the server
  const [draftConflict, setDraftConflict] = useState(null);  // newer draft saved by someone else
  const [activity, setActivity] = useState({ comments: [], history: [] });
  const applyActivity = (d) => setActivity({ comments: d?.comments || [], history: d?.history || [] });
  const draftVersion = useRef(null);
  const baseline = useRef(null);   // page defaults, so the first save can log what already differs
  const { user: me } = useAuth();
  const [readiness, setReadiness] = useState(null);
  const [payrollReadiness, setPayrollReadiness] = useState(null);
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const nudgeAck = useRef(false);
  const isClientPath = location.pathname.startsWith("/client");
  const canRunEdit = useClientPerms().canEdit("run_payroll");
  const suppressNextDraft = useRef(false);
  const draftActive = useRef(false);
  const suppressSave = useRef(false);
  // Only show employees whose hire/termination dates fall within the selected pay period.
  const excludedSet = new Set(excluded);
  const inScope = employees.filter((e) =>
    (!e.hire_date || !meta.pay_period_end || e.hire_date <= meta.pay_period_end) &&
    (!e.termination_date || !meta.pay_period_start || e.termination_date >= meta.pay_period_start)
  ).sort((a, b) => (a.last_name || "").localeCompare(b.last_name || "") || (a.first_name || "").localeCompare(b.first_name || ""));
  const visibleEmployees = inScope.filter((e) => !excludedSet.has(e.employee_id));
  const removedEmployees = inScope.filter((e) => excludedSet.has(e.employee_id));
  // Quick-remove candidates: hourly staff with no hours this run, or anyone previewing at $0 net.
  const zeroCandidates = visibleEmployees.filter((e) => {
    const p = preview?.payslips?.find((x) => x.employee_id === e.employee_id);
    const noHours = e.pay_type !== "salary" && !(parseFloat(hours[e.employee_id]) > 0);
    return noHours || (p && Math.abs(p.net_pay || 0) < 0.005);
  });
  // Phase 7 — payroll hours source (company default, overridable for this run only)
  const selectedCompany = companies.find((c) => c.company_id === companyId);
  const companyHoursDefault = selectedCompany?.payroll_hours_source === "approved_time" ? "approved_time" : "manual";
  const useTimeCodes = runSource === "approved_time";
  const sourceOverridden = runSource !== companyHoursDefault;
  const SOURCE_LABEL = { approved_time: "Approved PostWage Time & Leave", manual: "Manual Payroll Entry" };
  const timeEmps = Object.keys(timeBreakdown);
  const timeAttention = timeEmps.filter((eid) => (timeBreakdown[eid]?.warnings || []).length > 0);

  useEffect(() => {
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      const qs = new URLSearchParams(location.search).get("company");
      if (qs) setCompanyId(qs);
      else if (r.data[0]) setCompanyId(r.data[0].company_id);
    });
  }, [location.search]);

  // Migration readiness for the pay-date's tax year (compact, point-of-run indicator).
  const payYear = (meta.pay_date || "").slice(0, 4) || String(new Date().getFullYear());
  useEffect(() => {
    if (!companyId) { setReadiness(null); return; }
    nudgeAck.current = false;
    api.get(`/companies/${companyId}/migration/readiness?tax_year=${payYear}`)
      .then((r) => setReadiness(r.data)).catch(() => setReadiness(null));
  }, [companyId, payYear]);
  const reviewMigration = () => navigate(`${isClientPath ? "/client" : ""}/migration?company=${companyId}`);

  // Phase 8 — authoritative combined payroll readiness (company + per-employee) for this pay date.
  useEffect(() => {
    if (!companyId) { setPayrollReadiness(null); return; }
    const pd = meta.pay_date ? `?pay_date=${meta.pay_date}` : "";
    api.get(`/companies/${companyId}/payroll-readiness${pd}`)
      .then((r) => setPayrollReadiness(r.data)).catch(() => setPayrollReadiness(null));
  }, [companyId, meta.pay_date, employees]);

  const loadEmployees = useCallback(() => {
    if (!companyId) return;
    api.get(`/employees?company_id=${companyId}`).then(async (r) => {
      const active = r.data.filter((e) => e.status === "active");
      setEmployees(active);
      const co = companies.find((c) => c.company_id === companyId);
      if (co) setMeta((m) => ({ ...m, pay_frequency: co.default_pay_frequency }));
      const qsched = new URLSearchParams(location.search).get("schedule");
      const sched = qsched && (co?.schedules || []).find((s) => s.name === qsched);
      const first = (co?.schedules || [])[0];
      if (sched) { setScheduleName(sched.name); setMeta((m) => ({ ...m, pay_frequency: sched.frequency })); }
      else if (first) { setScheduleName(first.name); setMeta((m) => ({ ...m, pay_frequency: first.frequency })); }
      else setScheduleName("");
      const h = {};
      active.forEach((e) => { h[e.employee_id] = e.standard_hours; });
      const pm = {};
      active.forEach((e) => { pm[e.employee_id] = /manual|cheque|check/i.test(e.pay_method || "") ? "Manual" : "Direct Deposit"; });
      baseline.current = { hours: h, payMethods: pm, applyHoliday: true, excluded: [] };
      let draft = null, srv = null;
      try { const d = (await api.get(`/payroll/draft?company_id=${companyId}`)).data; srv = d.draft; applyActivity(d); } catch { srv = null; }
      if (srv) { draft = srv.payload; draftVersion.current = srv.version; setDraftMeta(srv); }
      else {
        draftVersion.current = null; setDraftMeta(null);
        try { const raw = localStorage.getItem(`rp_draft_${companyId}`); if (raw) draft = JSON.parse(raw); } catch { draft = null; }
        if (draft) {   // one-time migration of a legacy browser-only draft to the shared server draft
          try { const r = await api.put("/payroll/draft", { company_id: companyId, draft }); draftVersion.current = r.data.draft.version; setDraftMeta(r.data.draft); applyActivity(r.data); } catch {}
          try { localStorage.removeItem(`rp_draft_${companyId}`); } catch {}
        }
      }
      setDraftConflict(null);
      if (draft) {
        suppressNextDraft.current = true;
        suppressSave.current = true;
        draftActive.current = true;
        setHours(draft.hours || h);
        setPayMethods(draft.payMethods || pm);
        setPayouts(draft.payouts || {});
        setPayoutAmounts(draft.payoutAmounts || {});
        setPayoutReasons(draft.payoutReasons || {});
        setApplyHoliday(draft.applyHoliday ?? true);
        setHolidayOverrides(draft.holidayOverrides || {});
        setOtOverrides(draft.otOverrides || {});
        setOtChoice(draft.otChoice || {});
        setHolidayChoice(draft.holidayChoice || {});
        setVacOverrides(draft.vacOverrides || {});
        setVacReasons(draft.vacReasons || {});
        setLineOverrides(draft.lineOverrides || {});
        setAdjustments(draft.adjustments || {});
        setExcluded(draft.excluded || []);
        setSupersedesRunId(draft.supersedesRunId || null);
        if (draft.scheduleName != null) setScheduleName(draft.scheduleName);
        if (draft.meta) setMeta((m) => ({ ...m, ...draft.meta }));
        setDraftSavedAt(draft.savedAt || Date.now());
      } else {
        suppressSave.current = true;
        draftActive.current = false;
        setHours(h);
        setPayMethods(pm);
        setHolidayChoice({});
        setVacOverrides({});
        setVacReasons({});
        setLineOverrides({});
        setExcluded([]);
        setSupersedesRunId(null);
        setDraftSavedAt(null);
      }
      setPreview(null);
    });
  }, [companyId, companies]);

  useEffect(() => { loadEmployees(); }, [loadEmployees]);

  // Default the run's hours source to the company's configured default whenever the company changes.
  useEffect(() => {
    const c = companies.find((x) => x.company_id === companyId);
    setRunSource(c?.payroll_hours_source === "approved_time" ? "approved_time" : "manual");
    setShowSourceChange(false);
    setSourceReason("");
  }, [companyId, companies]);

  useEffect(() => {
    if (!companyId || !meta.pay_frequency) return;
    const wasSuppressed = suppressNextDraft.current;
    suppressNextDraft.current = false;
    api.get(`/payroll/next-draft?company_id=${companyId}&pay_frequency=${meta.pay_frequency}&schedule_name=${encodeURIComponent(scheduleName)}`)
      .then((r) => {
        setNextInfo(r.data);
        if (wasSuppressed || draftActive.current) {
          // A saved local draft is only kept if its pay date is still ahead of the last effective run for this schedule.
          setMeta((m) => {
            const stale = r.data.based_on && m.pay_date && m.pay_date <= r.data.based_on && !supersedesRunId;
            if (!stale) return m;
            draftActive.current = false;
            api.delete(`/payroll/draft?company_id=${companyId}`).catch(() => {});
            draftVersion.current = null; setDraftMeta(null);
            setDraftSavedAt(null);
            return { ...m, pay_period_start: r.data.pay_period_start, pay_period_end: r.data.pay_period_end, pay_date: r.data.pay_date };
          });
          return;
        }
        setMeta((m) => ({ ...m, pay_period_start: r.data.pay_period_start, pay_period_end: r.data.pay_period_end, pay_date: r.data.pay_date }));
      })
      .catch(() => {});
  }, [companyId, meta.pay_frequency, scheduleName]); // eslint-disable-line react-hooks/exhaustive-deps

  const body = useCallback(() => ({
    company_id: companyId, ...meta, employee_hours: hours,
    vacation_payouts: Object.keys(payouts).filter((k) => payouts[k]),
    vacation_payout_amounts: payoutAmounts,
    vacation_payout_reasons: payoutReasons,
    adjustments, schedule_name: offCycle ? "" : scheduleName, off_cycle: offCycle, off_cycle_type: offCycle ? offCycleType : "",
    apply_holiday_pay: applyHoliday, holiday_overrides: holidayOverrides, overtime_overrides: otOverrides, overtime_choice: otChoice,
    holiday_pay_choice: holidayChoice,
    vacation_accrual_overrides: vacOverrides,
    vacation_override_reasons: vacReasons,
    line_overrides: lineOverrides,
    payment_methods: payMethods,
    excluded_employee_ids: excluded,
    supersedes_run_id: supersedesRunId,
    hours_source_override: runSource,
    hours_source_override_reason: sourceReason,
  }), [companyId, meta, hours, payouts, payoutAmounts, payoutReasons, adjustments, scheduleName, applyHoliday, holidayOverrides, otOverrides, otChoice, holidayChoice, vacOverrides, vacReasons, lineOverrides, payMethods, excluded, supersedesRunId, runSource, sourceReason]);

  // Live totals: debounced auto-preview from the CRA payroll engine whenever inputs change.
  useEffect(() => {
    if (!companyId || !employees.length) return;
    const t = setTimeout(async () => {
      setPreviewing(true);
      try {
        const { data } = await api.post("/payroll/preview", body());
        setPreview(data);
      } catch { /* ignore transient live-preview errors */ }
      finally { setPreviewing(false); }
    }, 500);
    return () => clearTimeout(t);
  }, [companyId, employees, body]);

  // Phase 7: when "use approved time" is on, load the coded time breakdown for display.
  const [timeTick, setTimeTick] = useState(0);
  useEffect(() => {
    if (!companyId || !useTimeCodes) { setTimeBreakdown({}); return; }
    api.get(`/companies/${companyId}/timesheets/payroll-hours?start=${meta.pay_period_start}&end=${meta.pay_period_end}`)
      .then((r) => setTimeBreakdown(r.data.employees || {})).catch(() => setTimeBreakdown({}));
  }, [companyId, useTimeCodes, meta.pay_period_start, meta.pay_period_end, timeTick]);
  // Unapproved time (Approval Required ON) is excluded from payroll until approved — surface it loudly.
  const unapproved = useTimeCodes ? employees.filter((e) => (timeBreakdown[e.employee_id]?.warnings || []).some((w) => w.kind === "unapproved_time")) : [];
  const bulkApproveTime = async () => {
    if (!window.confirm(`Approve all pending timesheets for ${meta.pay_period_start} → ${meta.pay_period_end} for payroll now?\nWeeks with missing punches or other issues are skipped.`)) return;
    try {
      const { data } = await api.post(`/companies/${companyId}/timesheet-periods/bulk-payroll-approve`, { start: meta.pay_period_start, end: meta.pay_period_end, comment: "Approved from Run Payroll" });
      toast.success(`${data.approved} timesheet week${data.approved === 1 ? "" : "s"} approved for payroll${data.skipped_with_issues_or_final ? ` · ${data.skipped_with_issues_or_final} skipped` : ""}`);
      setTimeTick((t) => t + 1);
    } catch (e) { toast.error(e?.response?.data?.detail?.message || apiErr(e)); }
  };

  // Shared draft: persist the in-progress run on the server (debounced) so colleagues on other
  // computers can review it before Finalize. Last writer wins; a stale base version gets a reload banner.
  useEffect(() => {
    if (!companyId || !employees.length || !canRunEdit) return;
    if (suppressSave.current) { suppressSave.current = false; return; }
    const t = setTimeout(async () => {
      const savedAt = Date.now();
      const draft = {
        hours, payMethods, payouts, payoutAmounts, payoutReasons,
        applyHoliday, holidayOverrides, otOverrides, otChoice, holidayChoice, vacOverrides, vacReasons, lineOverrides, adjustments, scheduleName, meta, savedAt, excluded, supersedesRunId,
      };
      try {
        const r = await api.put("/payroll/draft", { company_id: companyId, draft, base_version: draftVersion.current, baseline: draftVersion.current == null ? baseline.current : undefined });
        draftVersion.current = r.data.draft.version; setDraftMeta(r.data.draft); setDraftSavedAt(savedAt); setDraftConflict(null); applyActivity(r.data);
      } catch (e) {
        if (e?.response?.status === 409) setDraftConflict(e.response.data?.detail || {});
        else toast.error(apiErr(e));
      }
    }, 800);
    return () => clearTimeout(t);
  }, [companyId, employees, hours, payMethods, payouts, payoutAmounts, payoutReasons, applyHoliday, holidayOverrides, otOverrides, otChoice, holidayChoice, vacOverrides, vacReasons, lineOverrides, adjustments, scheduleName, meta, excluded, supersedesRunId, canRunEdit]);

  // Poll so reviewers see when a colleague saves a newer shared draft.
  useEffect(() => {
    if (!companyId) return;
    const id = setInterval(async () => {
      try {
        const res = (await api.get(`/payroll/draft?company_id=${companyId}`)).data; const d = res.draft;
        applyActivity(res);
        if (d && draftVersion.current != null && d.version > draftVersion.current && d.saved_by !== me?.email) setDraftConflict(d);
      } catch {}
    }, 30000);
    return () => clearInterval(id);
  }, [companyId, me?.email]);

  const clearDraft = async () => {
    if (companyId) { try { await api.delete(`/payroll/draft?company_id=${companyId}`); } catch (e) { toast.error(apiErr(e)); return; } }
    draftVersion.current = null; setDraftMeta(null); setDraftConflict(null); setActivity({ comments: [], history: [] });
    draftActive.current = false;
    setSupersedesRunId(null);
    setDraftSavedAt(null);
    loadEmployees();
    toast.success("Draft cleared");
  };

  const holidayReport = async () => {
    try {
      const res = await api.post("/payroll/holiday-report-preview", body(), { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const fillFromTimesheets = async () => {
    try {
      const { data } = await api.get(`/companies/${companyId}/timesheets/summary?start=${meta.pay_period_start}&end=${meta.pay_period_end}`);
      const got = data.hours || {};
      if (!Object.keys(got).length) { toast.info("No timesheet hours found for this pay period."); return; }
      setHours((h) => ({ ...h, ...got }));
      toast.success("Hours filled from timesheets");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const applyHolidayOverride = async (empId, amount) => {
    const updated = { ...holidayOverrides, [empId]: amount };
    setHolidayOverrides(updated);
    try {
      const { data } = await api.post("/payroll/preview", { ...body(), holiday_overrides: updated });
      setPreview(data);
      setDetail(data.payslips.find((p) => p.employee_id === empId) || null);
      toast.success("Holiday pay updated");
    } catch {
      toast.error("Could not update holiday pay");
    }
  };

  const applyOtOverride = async (empId, hoursVal) => {
    const updated = { ...otOverrides };
    if (hoursVal === "" || hoursVal == null) delete updated[empId]; else updated[empId] = hoursVal;
    setOtOverrides(updated);
    try {
      const { data } = await api.post("/payroll/preview", { ...body(), overtime_overrides: updated });
      setPreview(data);
      setDetail(data.payslips.find((p) => p.employee_id === empId) || null);
      toast.success(hoursVal === "" ? "Overtime back to automatic" : "Overtime hours updated");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const applyOtChoice = async (empId, val) => {
    const updated = { ...otChoice, [empId]: val };
    setOtChoice(updated);
    try {
      const { data } = await api.post("/payroll/preview", { ...body(), overtime_choice: updated });
      setPreview(data);
      setDetail(data.payslips.find((p) => p.employee_id === empId) || null);
      toast.success(val === "lieu" ? "Overtime banked as time in lieu (1.5 h per OT hour)" : "Overtime paid at 1.5×");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const setHolChoice = async (empId, val) => {
    const updated = { ...holidayChoice, [empId]: val };
    setHolidayChoice(updated);
    try {
      const { data } = await api.post("/payroll/preview", { ...body(), holiday_pay_choice: updated });
      setPreview(data);
      setDetail(data.payslips.find((p) => p.employee_id === empId) || null);
      toast.success(val === "substitute" ? "Substitute day banked" : "Premium pay (1.5×) applied");
    } catch {
      toast.error("Could not update holiday option");
    }
  };

  const setLineOverride = async (empId, override) => {
    const prev = lineOverrides[empId] || {};
    const merged = { ...override, statutory: { ...(prev.statutory || {}), ...(override.statutory || {}) } };
    const upd = { ...lineOverrides, [empId]: merged };
    setLineOverrides(upd);
    try {
      const { data } = await api.post("/payroll/preview", { ...body(), line_overrides: upd });
      setPreview(data);
      setDetail(data.payslips.find((p) => p.employee_id === empId) || null);
      toast.success("Earnings / deductions updated");
    } catch {
      toast.error("Could not apply changes");
    }
  };

  const resetLine = async (empId) => {
    const upd = { ...lineOverrides }; delete upd[empId];
    setLineOverrides(upd);
    try {
      const { data } = await api.post("/payroll/preview", { ...body(), line_overrides: upd });
      setPreview(data);
      setDetail(data.payslips.find((p) => p.employee_id === empId) || null);
      toast.success("Reverted to CRA-calculated");
    } catch {
      toast.error("Could not revert");
    }
  };

  const setVacOverride = async (empId, amount, reason) => {
    const upd = { ...vacOverrides };
    if (amount === "" || amount == null) delete upd[empId]; else upd[empId] = amount;
    setVacOverrides(upd);
    const ur = { ...vacReasons, [empId]: reason ?? vacReasons[empId] ?? "" };
    setVacReasons(ur);
    try {
      const { data } = await api.post("/payroll/preview", { ...body(), vacation_accrual_overrides: upd, vacation_override_reasons: ur });
      setPreview(data);
      setDetail(data.payslips.find((p) => p.employee_id === empId) || null);
      toast.success(amount === "" || amount == null ? "Vacation override cleared" : "Vacation override applied");
    } catch {
      toast.error("Could not apply vacation override");
    }
  };

  const applyAdjustment = async (empId, line, kind) => {
    const cur = adjustments[empId] || { earnings: [], deductions: [] };
    const next = {
      ...cur,
      earnings: kind === "earning" ? [...(cur.earnings || []), line] : (cur.earnings || []),
      deductions: kind === "deduction" ? [...(cur.deductions || []), line] : (cur.deductions || []),
    };
    const updated = { ...adjustments, [empId]: next };
    setAdjustments(updated);
    try {
      const { data } = await api.post("/payroll/preview", { ...body(), adjustments: updated });
      setPreview(data);
      setDetail(data.payslips.find((p) => p.employee_id === empId) || null);
      toast.success("One-off applied");
    } catch {
      toast.error("Could not apply adjustment");
    }
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payroll/preview", body());
      setPreview(data);
      if (!data.payslips.length) toast.info("No active employees to preview.");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const finalize = async () => {
    if (sourceOverridden && !sourceReason.trim()) {
      toast.error("Please provide a reason for changing the payroll hours source for this run.");
      return;
    }
    // Soft nudge on the first live payroll when migration review is incomplete (orange).
    // Never blocks; shown once per company/session. Red is blocked authoritatively by the backend.
    if (readiness?.state === "orange" && !nudgeAck.current) {
      setNudgeOpen(true);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/payroll/run", body());
      toast.success(supersedesRunId ? "Payroll recalculated — review, approve & post" : "Payroll calculated — review, approve & post");
      setSupersedesRunId(null);
      suppressSave.current = true;
      if (companyId) api.delete(`/payroll/draft?company_id=${companyId}`).catch(() => {});
      navigate(`/history/${data.run.run_id}`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };
  const continuePayroll = () => { nudgeAck.current = true; setNudgeOpen(false); finalize(); };

  const slipById = {};
  (preview?.payslips || []).forEach((p) => { slipById[p.employee_id] = p; });
  const grossOf = (p) => (p ? (p.gross || 0) + (p.nontaxable_income || 0) : 0);
  const totalGross = preview ? ((preview.totals.gross || 0) + (preview.totals.nontaxable_income || 0)) : 0;
  const totalDed = preview ? (totalGross - preview.totals.net_pay) : 0;
  const totalHours = visibleEmployees.reduce((t, e) => t + (parseFloat(hours[e.employee_id]) || 0), 0);
  // Phase 5.2b-2b — backend-derived warnings & employer cost (never recomputed on the client).
  const negNet = (preview?.payslips || []).filter((p) => p.negative_net);
  const priorSysWarn = (preview?.payslips || []).flatMap((p) => (p.deduction_annual_warnings || []).map((w) => ({ ...w, employee_name: p.employee_name })));
  const empCost = (preview?.payslips || []).reduce((a, p) => ({
    gross: a.gross + ((p.gross || 0) + (p.nontaxable_income || 0)),
    cpp: a.cpp + (p.employer_cpp || 0), cpp2: a.cpp2 + (p.employer_cpp2 || 0),
    ei: a.ei + (p.employer_ei || 0), match: a.match + (p.employer_match_total || 0),
    total: a.total + (p.employer_cost || 0),
  }), { gross: 0, cpp: 0, cpp2: 0, ei: 0, match: 0, total: 0 });
  const peopleHref = `${isClientPath ? "/client" : ""}/people?company=${companyId}`;
  const glReady = preview?.gl_readiness;
  const settingsHref = `/settings?company=${companyId}&tab=codes`;

  return (
    <div className="w-full">
      <CompanyBreadcrumb label="Run Payroll" />
      {draftConflict && (
        <div data-testid="draft-conflict-banner" className="mb-6 border border-amber-500 bg-amber-50 text-amber-900 px-4 py-3 text-sm flex flex-wrap items-center justify-between gap-3">
          <span className="inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> A newer shared draft was saved by <strong>{draftConflict.saved_by_name || draftConflict.saved_by}</strong> at {fmtDateTime(draftConflict.saved_at)}. Your changes since then are not saved.</span>
          <span className="flex items-center gap-2">
            <button data-testid="draft-conflict-reload" onClick={() => { draftActive.current = false; loadEmployees(); }} className="border border-black bg-black text-white px-3 py-1.5 text-xs font-medium hover:bg-neutral-800 transition-colors">Load their draft</button>
            <button data-testid="draft-conflict-overwrite" onClick={() => { draftVersion.current = draftConflict.version; setDraftConflict(null); setHours((h) => ({ ...h })); }} className="border border-border px-3 py-1.5 text-xs hover:border-black transition-colors">Keep mine &amp; overwrite</button>
          </span>
        </div>
      )}
      <div className="mb-8">
        <h1 className="text-4xl font-black tracking-tight">Run Payroll</h1>
        <p className="text-sm text-muted-foreground mt-1 font-mono uppercase tracking-widest flex items-center gap-2">
          <Info className="h-3.5 w-3.5" /> CRA T4127 (Jan 2026) · Ontario ESA compliant · OT 1.5× after 44h/wk
        </p>
      </div>

      {readiness && !readiness.closed && readiness.state !== "green" && !(readiness.has_finalized_payroll && readiness.state !== "red") && (() => {
        const c = readiness.counts || {};
        const cfg = {
          green: { border: "border-emerald-500", bg: "bg-emerald-50", text: "text-emerald-800", Icon: CheckCircle2, msg: "All active payroll employees are ready." },
          orange: { border: "border-amber-500", bg: "bg-amber-50", text: "text-amber-800", Icon: AlertTriangle, msg: `${c.needs_review || 0} employee${(c.needs_review || 0) === 1 ? "" : "s"} still require migration review before first live payroll.` },
          red: { border: "border-red-500", bg: "bg-red-50", text: "text-red-800", Icon: AlertTriangle, msg: `${c.critical || 0} employee${(c.critical || 0) === 1 ? "" : "s"} have a blocking opening-balance overlap or other critical migration error.` },
        }[readiness.state] || {};
        const prominent = readiness.first_run;
        return (
          <div data-testid="run-readiness-indicator" data-state={readiness.state}
            className={`mb-6 border-l-4 ${cfg.border} ${cfg.bg} ${prominent ? "p-4" : "px-4 py-2.5"}`}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <cfg.Icon className={`h-4 w-4 ${cfg.text}`} />
              {prominent && <span data-testid="run-readiness-firstrun" className={`text-sm font-black tracking-tight ${cfg.text}`}>First PostWage Payroll —</span>}
              <span data-testid="run-readiness-label" className={`text-sm font-semibold ${cfg.text}`}>
                {readiness.label}
              </span>
              <span className="text-xs text-muted-foreground">{prominent ? "Migration readiness should be reviewed before processing the first live run. " : ""}{cfg.msg}</span>
              <button data-testid="run-readiness-review" onClick={reviewMigration} className={`ml-auto inline-flex items-center gap-1 text-xs border px-2.5 py-1 ${cfg.text} border-current hover:bg-black hover:text-white hover:border-black transition-colors`}>Review Migration</button>
            </div>
            {readiness.state !== "green" && (
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground" data-testid="run-readiness-counts">
                <span>Opening balances complete: <b>{c.opening_complete || 0}</b></span>
                <span>New hires confirmed: <b>{c.new_hire_confirmed || 0}</b></span>
                <span>Needs review: <b>{c.needs_review || 0}</b></span>
                <span>Critical conflicts: <b className={c.critical ? "text-red-600" : ""}>{c.critical || 0}</b></span>
              </div>
            )}
          </div>
        );
      })()}

      {payrollReadiness && payrollReadiness.overall_status !== "READY" && (() => {
        const pr = payrollReadiness;
        const compBlk = pr.company?.blockers || [];
        const compWarn = pr.company?.warnings || [];
        const empBlk = (pr.employees || []).filter((e) => e.status === "BLOCKED");
        const empWarn = (pr.employees || []).filter((e) => e.status === "WARNING");
        const blocked = pr.overall_status === "BLOCKED";
        return (
          <div data-testid="payroll-readiness-panel" data-state={pr.overall_status}
            className={`mb-6 border px-4 py-3 ${blocked ? "border-red-500 bg-red-50" : "border-amber-500 bg-amber-50"}`}>
            <div className="flex items-center gap-2 mb-2">
              {blocked ? <AlertTriangle className="h-4 w-4 text-red-700" /> : <Info className="h-4 w-4 text-amber-700" />}
              <span data-testid="payroll-readiness-status" className={`text-sm font-semibold ${blocked ? "text-red-800" : "text-amber-800"}`}>
                {blocked ? "Payroll blocked — resolve before calculating" : "Payroll ready with warnings"}
              </span>
              <span className="text-[11px] font-mono text-muted-foreground ml-auto">{empBlk.length} blocked · {empWarn.length} warning employee{empWarn.length === 1 ? "" : "s"}</span>
            </div>
            <div className="space-y-2 text-sm">
              {compBlk.map((b, i) => (
                <div key={`cb${i}`} className="flex items-start gap-2 text-red-800"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" /><span><span className="font-semibold">Company:</span> {b.detail}</span></div>
              ))}
              {compWarn.map((w, i) => (
                <div key={`cw${i}`} className="flex items-start gap-2 text-amber-800"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" /><span><span className="font-semibold">Company:</span> {w.detail}</span></div>
              ))}
              {empBlk.map((e) => (
                <div key={`eb${e.employee_id}`} data-testid={`payroll-readiness-blocked-${e.employee_id}`} className="flex items-start gap-2 text-red-800">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" />
                  <span><span className="font-semibold">{e.employee_name || e.employee_id}:</span> {(e.blockers || []).map((b) => b.detail).join(" · ")}</span>
                </div>
              ))}
              {empWarn.slice(0, 8).map((e) => (
                <div key={`ew${e.employee_id}`} className="flex items-start gap-2 text-amber-800">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
                  <span><span className="font-semibold">{e.employee_name || e.employee_id}:</span> {(e.warnings || []).map((b) => b.detail).join(" · ")}</span>
                </div>
              ))}
            </div>
            {blocked && <p className="mt-2 text-[11px] text-red-700">Fix these in People (or exclude the employee) — the payroll engine will reject the run until every blocker is cleared.</p>}
          </div>
        );
      })()}

      {supersedesRunId && (
        <div data-testid="reopened-run-banner" className="mb-6 flex items-start gap-3 border border-amber-300 bg-amber-50 px-4 py-3">
          <RotateCcw className="h-4 w-4 mt-0.5 text-amber-700 shrink-0" />
          <p className="text-sm text-amber-900">
            You're editing a <span className="font-semibold">reopened run</span>. The original stays in Payroll History until you click <span className="font-semibold">Finalize Run</span>, which replaces it. Use <span className="font-semibold">Clear draft</span> to abandon these edits and keep the original untouched.
          </p>
        </div>
      )}

      <div className="grid lg:grid-cols-4 gap-4">
        {/* Config panel */}
        <div className="border border-border bg-white p-5 space-y-4 lg:col-span-1 h-full">
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Client company</label>
            <div data-testid="run-company-name" className="mt-1.5 w-full border border-input bg-neutral-50 px-3 py-2.5 text-sm font-medium truncate">
              {companies.find((c) => c.company_id === companyId)?.name || "—"}
            </div>
          </div>
          {(() => {
            const co = companies.find((c) => c.company_id === companyId);
            const scheds = co?.schedules || [];
            const value = scheduleName ? `sched:${scheduleName}` : `freq:${meta.pay_frequency}`;
            return (
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Pay schedule &amp; frequency</label>
                <select data-testid="run-schedule-select" value={value} onChange={(e) => {
                  const v = e.target.value;
                  draftActive.current = false;
                  if (v.startsWith("sched:")) {
                    const sch = scheds.find((x) => x.name === v.slice(6));
                    setScheduleName(sch?.name || "");
                    if (sch) setMeta((m) => ({ ...m, pay_frequency: sch.frequency }));
                  } else {
                    setScheduleName("");
                    setMeta((m) => ({ ...m, pay_frequency: v.slice(5) }));
                  }
                }} className="mt-1.5 w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black">
                  {scheds.length > 0 && <optgroup label="Company pay schedules">
                    {scheds.map((sch, i) => <option key={i} value={`sched:${sch.name}`}>{sch.name} · {FREQ_LABELS[sch.frequency]}</option>)}
                  </optgroup>}
                  <optgroup label={scheds.length ? "Ad-hoc run (no schedule)" : "Frequency"}>
                    {Object.entries(FREQ_LABELS).map(([k, v]) => <option key={k} value={`freq:${k}`}>{scheds.length ? `Ad-hoc · ${v}` : v}</option>)}
                  </optgroup>
                </select>
                <label className="mt-2 flex items-center gap-2 text-xs cursor-pointer">
                  <input data-testid="run-off-cycle" type="checkbox" checked={offCycle} onChange={(e) => setOffCycle(e.target.checked)} className="h-4 w-4 accent-black" />
                  <span>Off-cycle run <span className="text-muted-foreground">(bonus / correction — does not shift the schedule's next date)</span></span>
                </label>
                {offCycle && (
                  <select data-testid="run-off-cycle-type" value={offCycleType} onChange={(e) => setOffCycleType(e.target.value)} className="mt-1.5 w-full border border-input bg-white px-3 py-2 text-sm">
                    <option value="bonus">Bonus</option><option value="correction">Correction / adjustment</option><option value="termination">Termination / final pay</option><option value="other">Other off-cycle</option>
                  </select>
                )}
                {!offCycle && nextInfo?.based_on && <p data-testid="run-next-info" className="mt-1 text-[11px] text-muted-foreground">Next in sequence after the {nextInfo.based_on} run{nextInfo.based_on_status && !/POSTED|PAID/.test(nextInfo.based_on_status) ? ` (${nextInfo.based_on_status.toLowerCase()} draft)` : ""}.</p>}
                {!offCycle && nextInfo?.due_by && (
                  <p data-testid="run-due-by" className={`mt-1 text-[11px] ${nextInfo.past_due_by ? "text-red-700 font-semibold" : "text-muted-foreground"}`}>
                    Due by {nextInfo.due_by} ({nextInfo.due_by_days} business day{nextInfo.due_by_days === 1 ? "" : "s"} before pay date)
                    {nextInfo.past_due_by && " — past the processor deadline: payment to employees may be delayed."}
                  </p>
                )}
              </div>
            );
          })()}
          <DateField label="Period start" testid="run-period-start" value={meta.pay_period_start} onChange={(v) => setMeta({ ...meta, pay_period_start: v })} />
          <DateField label="Period end" testid="run-period-end" value={meta.pay_period_end} onChange={(v) => setMeta({ ...meta, pay_period_end: v })} />
          <DateField label="Pay date" testid="run-pay-date" value={meta.pay_date} onChange={(v) => setMeta({ ...meta, pay_date: v })} />

          {visibleEmployees.some((e) => (e.vacation_balance || 0) > 0) && (
            <details className="pt-2 border-t border-border group" data-testid="vacation-payout-section">
              <summary className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2 cursor-pointer list-none flex items-center justify-between hover:text-black">
                <span>Vacation payout (banked) · {visibleEmployees.filter((e) => (e.vacation_balance || 0) > 0).length} eligible{Object.values(payouts).filter(Boolean).length ? ` · ${Object.values(payouts).filter(Boolean).length} selected` : ""}</span>
                <span className="group-open:rotate-180 transition-transform">▾</span>
              </summary>
              {visibleEmployees.filter((e) => (e.vacation_balance || 0) > 0).map((e) => (
                <div key={e.employee_id} className="mb-2">
                  <label className="flex items-center justify-between gap-2 text-xs cursor-pointer">
                    <span className="flex items-center gap-2">
                      <input data-testid={`payout-${e.employee_id}`} type="checkbox" checked={!!payouts[e.employee_id]} onChange={(ev) => {
                        const on = ev.target.checked;
                        setPayouts({ ...payouts, [e.employee_id]: on });
                        setPayoutAmounts((pa) => { const n = { ...pa }; if (on) n[e.employee_id] = e.vacation_balance; else delete n[e.employee_id]; return n; });
                      }} className="h-4 w-4 accent-black" />
                      {e.first_name} {e.last_name}
                    </span>
                    <span className="tabular-nums text-emerald-600">{money(e.vacation_balance)}</span>
                  </label>
                  {payouts[e.employee_id] && (
                    <div className="flex items-center justify-between gap-2 mt-1 pl-6">
                      <span className="text-[10px] text-muted-foreground">Payout amount (max {money(e.vacation_balance)})</span>
                      <input data-testid={`payout-amount-${e.employee_id}`} type="number" value={payoutAmounts[e.employee_id] ?? ""} max={e.vacation_balance}
                        onChange={(ev) => setPayoutAmounts({ ...payoutAmounts, [e.employee_id]: ev.target.value })}
                        className="w-24 border border-input px-2 py-1 text-xs text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-black" />
                    </div>
                  )}
                  {payouts[e.employee_id] && (
                    <div className="mt-1 pl-6">
                      <input data-testid={`payout-reason-${e.employee_id}`} placeholder="Reason / note (for audit)" value={payoutReasons[e.employee_id] ?? ""}
                        onChange={(ev) => setPayoutReasons({ ...payoutReasons, [e.employee_id]: ev.target.value })}
                        className="w-full border border-input px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-black" />
                    </div>
                  )}
                </div>
              ))}
            </details>
          )}

          <label className="flex items-center gap-2 text-xs cursor-pointer pt-2 border-t border-border">
            <input data-testid="apply-holiday-toggle" type="checkbox" checked={applyHoliday} onChange={(e) => setApplyHoliday(e.target.checked)} className="h-4 w-4 accent-black" />
            Apply statutory holiday pay (hourly, ESA 4-wk lookback)
          </label>

          <div className="pt-2 border-t border-border space-y-2" data-testid="payroll-hours-source">
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono uppercase tracking-widest text-[10px] text-muted-foreground">Payroll Hours Source</span>
              <button data-testid="change-hours-source-button" type="button"
                onClick={() => { setShowSourceChange((v) => !v); if (showSourceChange) { setRunSource(companyHoursDefault); setSourceReason(""); } }}
                className="text-[10px] font-mono uppercase tracking-widest border border-border px-2 py-1 hover:border-black transition-colors">
                {showSourceChange ? "Cancel" : "Change for this payroll"}
              </button>
            </div>
            <div data-testid="hours-source-current" className="flex items-center gap-2 text-sm font-medium">
              {useTimeCodes ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <FileText className="h-4 w-4 text-muted-foreground" />}
              {SOURCE_LABEL[runSource]}
            </div>
            <p className="text-[11px] text-muted-foreground">
              {useTimeCodes
                ? "Approved timesheets will automatically be included in this payroll."
                : "Hours and earnings will not be automatically imported from Time & Leave."}
            </p>

            {showSourceChange && (
              <div className="border border-border bg-muted/30 p-2 space-y-2" data-testid="hours-source-override">
                {["approved_time", "manual"].map((opt) => (
                  <label key={opt} className="flex items-start gap-2 text-xs cursor-pointer">
                    <input data-testid={`hours-source-option-${opt}`} type="radio" name="hours_source" className="mt-0.5 h-3.5 w-3.5 accent-black"
                      checked={runSource === opt} onChange={() => setRunSource(opt)} />
                    <span>
                      <span className="font-medium">{SOURCE_LABEL[opt]}</span>
                      <span className="block text-[11px] text-muted-foreground">
                        {opt === "approved_time"
                          ? "Automatically use approved regular hours, overtime, vacation and applicable paid leave when building payroll."
                          : "Enter payroll hours and earnings manually without automatically importing approved Time & Leave entries."}
                      </span>
                    </span>
                  </label>
                ))}
                {sourceOverridden && (
                  <>
                    <div data-testid="hours-source-override-warning" className="border border-amber-300 bg-amber-50 text-amber-800 text-[11px] px-2 py-1.5 flex gap-1.5">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                      <span>Payroll hours source changed for this run. The company normally uses <b>{SOURCE_LABEL[companyHoursDefault]}</b>. This payroll is currently using <b>{SOURCE_LABEL[runSource]}</b>.</span>
                    </div>
                    <div>
                      <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Reason (required)</label>
                      <select data-testid="hours-source-reason-select" value={["Timesheets processed externally", "Correction payroll", "Final payroll", "Manual adjustment required"].includes(sourceReason) ? sourceReason : (sourceReason ? "Other" : "")}
                        onChange={(e) => setSourceReason(e.target.value === "Other" ? " " : e.target.value)}
                        className="w-full border border-border bg-white px-2 py-1 text-xs mt-0.5">
                        <option value="">Select a reason…</option>
                        <option>Timesheets processed externally</option>
                        <option>Correction payroll</option>
                        <option>Final payroll</option>
                        <option>Manual adjustment required</option>
                        <option value="Other">Other</option>
                      </select>
                      <textarea data-testid="hours-source-reason-text" value={sourceReason} onChange={(e) => setSourceReason(e.target.value)}
                        placeholder="Explain why the hours source is changing for this payroll" rows={2}
                        className="w-full border border-border bg-white px-2 py-1 text-xs mt-1" />
                    </div>
                  </>
                )}
              </div>
            )}

            {useTimeCodes && timeEmps.length > 0 && (
              <div className="text-[11px] text-muted-foreground" data-testid="time-readiness-summary">
                <span className="font-medium text-foreground">{timeEmps.length}</span> employee{timeEmps.length === 1 ? "" : "s"} with approved time
                {timeAttention.length > 0 && <> · <span className="text-amber-700 font-medium">{timeAttention.length} require attention</span></>}
              </div>
            )}
          </div>

          {unapproved.length > 0 && (
            <div className="border-2 border-amber-500 bg-amber-50 p-3 text-xs space-y-2" data-testid="unapproved-time-banner">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold text-amber-900">{unapproved.length} employee{unapproved.length === 1 ? " has" : "s have"} hours not yet approved for payroll</div>
                  <div className="text-amber-800 mt-0.5">"Approval Required" is on for this company, so timesheet hours only flow into Run Payroll after they are submitted and approved for payroll. Unapproved hours are excluded from this run.</div>
                  <div className="text-amber-800 mt-1">{unapproved.map((e) => `${e.first_name} ${e.last_name}`).join(", ")}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {canRunEdit && <button data-testid="bulk-approve-time" onClick={bulkApproveTime} className="border border-black bg-black text-white px-3 py-1.5 text-xs font-medium hover:bg-white hover:text-black transition-colors">Approve all pending weeks for payroll</button>}
                <a data-testid="goto-time-approvals" href={`${isClientPath ? "/client" : ""}/time?company=${companyId}&tab=approvals`} className="border border-amber-700 text-amber-900 px-3 py-1.5 text-xs font-medium hover:bg-amber-700 hover:text-white transition-colors">Review in Time → Approvals</a>
              </div>
            </div>
          )}

          {useTimeCodes && Object.keys(timeBreakdown).length > 0 && (
            <div className="border border-border bg-muted/30 p-2 text-[11px] space-y-1" data-testid="time-breakdown">
              <div className="font-mono uppercase tracking-widest text-muted-foreground">Approved time → earnings</div>
              {employees.filter((e) => timeBreakdown[e.employee_id]).map((e) => {
                const a = timeBreakdown[e.employee_id];
                return (
                  <div key={e.employee_id} data-testid={`time-breakdown-${e.employee_id}`} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 border-t border-border pt-1">
                    <span className="font-medium">{e.first_name} {e.last_name}</span>
                    {a.by_category && Object.entries(a.by_category).map(([cat, v]) => (
                      <span key={cat} className="text-muted-foreground">{cat.toLowerCase()}: {(v.worked || v.paid || 0)}h</span>
                    ))}
                    <span className="text-muted-foreground">· insurable {a.insurable_hours}h · ROE {a.roe_hours}h</span>
                    {(a.warnings || []).map((w, i) => <span key={i} title={w.detail} className="text-[9px] font-mono uppercase border border-amber-500 bg-amber-50 text-amber-700 px-1">{w.kind}</span>)}
                  </div>
                );
              })}
            </div>
          )}

          <button data-testid="preview-payroll-button" onClick={runPreview} disabled={busy || !companyId} className="w-full border border-black bg-white py-3 text-sm font-medium flex items-center justify-center gap-2 hover:bg-black hover:text-white transition-colors disabled:opacity-50">
            <Calculator className="h-4 w-4" /> Recalculate
          </button>
          <div className="flex items-center justify-between gap-2 pt-1">
            <span data-testid="draft-status" className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              <Save className="h-3 w-3" /> {draftMeta ? `Shared draft · ${draftMeta.saved_by === me?.email ? "you" : (draftMeta.saved_by_name || draftMeta.saved_by)} · ${fmtDateTime(draftMeta.saved_at)}` : draftSavedAt ? "Draft auto-saved" : "Auto-saves as shared draft"}
            </span>
            {draftSavedAt && (
              <button data-testid="clear-draft-button" onClick={clearDraft} className="text-[10px] font-mono uppercase tracking-widest border border-border px-2 py-1 hover:border-red-600 hover:text-red-600 transition-colors">Clear draft</button>
            )}
          </div>
          {companyId && <DraftActivity companyId={companyId} comments={activity.comments} history={activity.history} onUpdated={applyActivity} me={me?.email} />}
        </div>

        {/* Per-employee run table */}
        <div className="lg:col-span-3">
          <div className="border border-border bg-white">
            {preview?.exceptions_review && preview.exceptions_review.status !== "CLEAR" && (
              <div data-testid="preview-exceptions-summary"
                className={`border-b px-5 py-3 ${preview.exceptions_review.status === "BLOCKED" ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-mono uppercase tracking-widest ${preview.exceptions_review.status === "BLOCKED" ? "text-red-700" : "text-amber-700"}`}>
                    Payroll exceptions — {preview.exceptions_review.status}
                  </span>
                  <span className="text-[11px] font-mono text-muted-foreground ml-auto">
                    {preview.exceptions_review.blocker_count} blocker{preview.exceptions_review.blocker_count === 1 ? "" : "s"} · {preview.exceptions_review.warning_count} warning{preview.exceptions_review.warning_count === 1 ? "" : "s"}
                  </span>
                </div>
                <ul className="text-sm space-y-0.5">
                  {preview.exceptions_review.exceptions.filter((e) => e.severity !== "INFO").slice(0, 8).map((e, i) => (
                    <li key={i} className={e.severity === "BLOCKER" ? "text-red-800" : "text-amber-800"}>• {e.message}</li>
                  ))}
                </ul>
                <p className="text-[11px] text-muted-foreground mt-1.5">Full review available on the run detail page after finalizing.</p>
              </div>
            )}
            {preview?.esa_warnings?.length > 0 && (
              <div data-testid="esa-warnings" className="border-b border-amber-300 bg-amber-50 px-5 py-3">
                <div className="text-xs font-mono uppercase tracking-widest text-amber-700 mb-1">ESA compliance warnings</div>
                <ul className="text-sm text-amber-800 space-y-0.5">
                  {preview.esa_warnings.map((w, i) => <li key={i}>• {w}</li>)}
                </ul>
              </div>
            )}
            {preview?.holidays && Object.keys(preview.holidays).length > 0 && (
              <div data-testid="holidays-banner" className="border-b border-blue-200 bg-blue-50 px-5 py-2.5 text-sm text-blue-800 flex flex-wrap items-center justify-between gap-2">
                <span>
                  <span className="font-medium">Public holiday{Object.keys(preview.holidays).length > 1 ? "s" : ""} this period:</span>{" "}
                  {Object.entries(preview.holidays).map(([n, d]) => `${n} (${d})`).join(", ")} — statutory holiday pay added.
                </span>
                <button data-testid="holiday-report-button" onClick={holidayReport} className="inline-flex items-center gap-1.5 border border-blue-700 text-blue-800 px-3 py-1.5 text-xs font-medium hover:bg-blue-700 hover:text-white transition-colors whitespace-nowrap">
                  <FileText className="h-3.5 w-3.5" /> Holiday pay audit (PDF)
                </button>
              </div>
            )}
            <div className="px-5 py-4 border-b border-border flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <h2 className="font-bold tracking-tight">Employees · {visibleEmployees.length}</h2>
                {previewing && <span data-testid="preview-calculating" className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground animate-pulse">calculating…</span>}
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="fill-from-timesheets" onClick={fillFromTimesheets} className="text-[11px] font-mono uppercase tracking-widest border border-border px-3 py-2 hover:border-black transition-colors">Fill from timesheets</button>
                {!canRunEdit && <span data-testid="run-payroll-readonly" className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">View only</span>}
                <button data-testid="finalize-payroll-button" onClick={finalize} disabled={busy || !canRunEdit || !preview?.payslips?.length} className="flex items-center gap-2 bg-black text-white px-5 py-2.5 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">
                  <CheckCircle2 className="h-4 w-4" /> Finalize Run
                </button>
              </div>
            </div>
            {(removedEmployees.length > 0 || zeroCandidates.length > 0) && (
              <div data-testid="removed-employees-bar" className="mx-5 mt-4 border border-amber-300 bg-amber-50 px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className="text-[11px] font-mono uppercase tracking-widest text-amber-800">{removedEmployees.length} removed from this run</span>
                  <div className="flex items-center gap-3">
                    {zeroCandidates.length > 0 && (
                      <button data-testid="remove-zero-employees" title={zeroCandidates.map((e) => `${e.first_name} ${e.last_name}`).join(", ")}
                        onClick={() => { setExcluded((x) => [...x, ...zeroCandidates.map((e) => e.employee_id)]); toast.success(`${zeroCandidates.length} employee${zeroCandidates.length === 1 ? "" : "s"} with zero hours / $0 net removed from this run`); }}
                        className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest border border-amber-400 bg-white px-2 py-1 text-amber-800 hover:bg-black hover:text-white hover:border-black transition-colors">
                        <X className="h-3 w-3" /> Remove {zeroCandidates.length} with zero hours / $0 net
                      </button>
                    )}
                    {removedEmployees.length > 0 && (
                      <button data-testid="restore-all-employees" onClick={() => setExcluded([])} className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-amber-800 hover:text-black transition-colors">
                        <RotateCcw className="h-3 w-3" /> Restore all
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {removedEmployees.map((e) => (
                    <button key={e.employee_id} data-testid={`restore-${e.employee_id}`} onClick={() => setExcluded((x) => x.filter((id) => id !== e.employee_id))} className="inline-flex items-center gap-1.5 border border-amber-300 bg-white px-2.5 py-1 text-xs hover:border-black transition-colors">
                      <RotateCcw className="h-3 w-3 text-amber-700" /> {e.first_name} {e.last_name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {negNet.length > 0 && (
              <div data-testid="negative-net-banner" className="mx-5 mt-4 border-2 border-red-600 bg-red-50 px-4 py-3">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-red-700" />
                  <h3 className="font-bold tracking-tight text-red-800">Negative Net Pay</h3>
                </div>
                <div className="divide-y divide-red-200">
                  {negNet.map((p) => (
                    <div key={p.employee_id} data-testid={`negative-net-${p.employee_id}`} className="py-2 text-sm text-red-900">
                      <div className="font-medium">{p.employee_name}</div>
                      <div className="font-mono text-xs mt-0.5">
                        {p.negative_net.code && <>{p.negative_net.code} — {money(p.negative_net.deduction_amount)} · Net before deduction: {money(p.negative_net.net_before_deduction)} · </>}
                        Resulting net: <span className="font-bold">{money(p.negative_net.resulting_net)}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-red-800 mt-2 font-medium">This payroll cannot be posted until corrected.</p>
              </div>
            )}
            {priorSysWarn.length > 0 && (
              <div data-testid="prior-system-warning-banner" className="mx-5 mt-4 border border-orange-400 bg-orange-50 px-4 py-3">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="h-4 w-4 text-orange-600" />
                  <h3 className="font-bold tracking-tight text-orange-800">Prior-system deduction YTD may be required</h3>
                </div>
                <p className="text-xs text-orange-800">These migrated employees have a deduction with an annual payroll limit but no prior-system deduction YTD on file. Prior-system deductions may need to be entered before the remaining limit can be confirmed.</p>
                <ul className="mt-2 text-xs text-orange-900 space-y-0.5">
                  {priorSysWarn.map((w, i) => (
                    <li key={i} className="font-mono">• {w.employee_name} · {w.deduction_code} · annual limit {money(w.annual_max)}</li>
                  ))}
                </ul>
                <a data-testid="goto-opening-ytd" href={peopleHref} className="inline-flex items-center gap-1 mt-2 text-xs font-medium border border-orange-500 text-orange-800 px-2.5 py-1 hover:bg-orange-500 hover:text-white transition-colors">Enter Opening Deduction YTD →</a>
              </div>
            )}
            {glReady && (
              glReady.ready ? (
                <div data-testid="gl-readiness-ready" className="mx-5 mt-4 border border-emerald-400 bg-emerald-50 px-4 py-2.5 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-800">GL Ready</span>
                  <span className="text-xs text-emerald-700">All coded payroll lines have the required accounting mappings.</span>
                </div>
              ) : (
                <div data-testid="gl-readiness-missing" className="mx-5 mt-4 border-2 border-red-600 bg-red-50 px-4 py-3">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-red-700" />
                      <span className="font-bold text-red-800">GL Mapping Required</span>
                      <span className="text-xs text-red-700">{glReady.missing_count} payroll code{glReady.missing_count === 1 ? "" : "s"} need{glReady.missing_count === 1 ? "s" : ""} account mapping before this payroll can be posted.</span>
                    </div>
                    {!isClientPath && (
                      <a data-testid="gl-fix-mapping" href={settingsHref} className="whitespace-nowrap text-xs font-medium border border-red-600 text-red-800 px-2.5 py-1 hover:bg-red-600 hover:text-white transition-colors">Fix GL Mapping →</a>
                    )}
                  </div>
                  <ul className="space-y-1">
                    {glReady.missing.map((m) => (
                      <li key={m.code} data-testid={`gl-missing-${m.code}`} className="text-xs text-red-900">
                        <span className="font-mono font-medium">{m.code} — {m.code_name}</span>
                        <span className="text-red-700"> · Missing: {m.missing_fields.join(" and ")}{m.affected_employees ? ` · ${m.affected_employees} employee${m.affected_employees === 1 ? "" : "s"}` : ""}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            )}
            {visibleEmployees.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                      <th className="px-4 py-3 sticky left-0 bg-white">Employee</th>
                      <th className="px-3 py-3 text-right">Hours</th>
                      <th className="px-3 py-3">Payment method</th>
                      <th className="px-3 py-3">Deposit</th>
                      <th className="px-4 py-3 text-right">Gross</th>
                      <th className="px-4 py-3 text-right">Deductions</th>
                      <th className="px-4 py-3 text-right">Net Pay</th>
                      <th className="px-3 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleEmployees.map((e) => {
                      const p = slipById[e.employee_id];
                      return (
                        <tr key={e.employee_id} data-testid={`run-row-${e.employee_id}`} className="border-b border-border last:border-0 hover:bg-muted/50">
                          <td className="px-4 py-3 sticky left-0 bg-white font-medium">
                            <button data-testid={`preview-row-${e.employee_id}`} onClick={() => p && setDetail(p)} disabled={!p} className="inline-flex items-center gap-1.5 text-left hover:underline disabled:no-underline disabled:cursor-default">
                              <span>{e.first_name} {e.last_name}</span>
                              {e.pay_type === "salary" && <span className="text-[9px] font-mono text-muted-foreground">·SAL</span>}
                              {p && <Eye className="h-3 w-3 text-muted-foreground" />}
                              {holidayOverrides[e.employee_id] != null && holidayOverrides[e.employee_id] !== "" && (
                                <span data-testid={`holiday-override-badge-${e.employee_id}`} className="ml-1 border border-amber-500 bg-amber-50 text-amber-700 text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5">holiday override</span>
                              )}
                              {p?.negative_net && (
                                <span data-testid={`negative-net-badge-${e.employee_id}`} className="ml-1 border border-red-600 bg-red-50 text-red-700 text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5">negative net</span>
                              )}
                            </button>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <input data-testid={`hours-${e.employee_id}`} type="number" value={hours[e.employee_id] ?? ""} onChange={(ev) => setHours({ ...hours, [e.employee_id]: ev.target.value })} className="w-20 border border-input px-2 py-1.5 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-black" />
                          </td>
                          <td className="px-3 py-2">
                            <select data-testid={`paymethod-${e.employee_id}`} value={payMethods[e.employee_id] || "Direct Deposit"} onChange={(ev) => setPayMethods({ ...payMethods, [e.employee_id]: ev.target.value })} className="w-36 border border-input bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-black">
                              <option value="Direct Deposit">Direct Deposit</option>
                              <option value="Manual">Manual cheque</option>
                            </select>
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap" data-testid={`deposit-${e.employee_id}`}>
                            {(payMethods[e.employee_id] || "Direct Deposit") === "Manual" ? (
                              <span className="text-muted-foreground">Cheque</span>
                            ) : (e.bank_account && String(e.bank_account).trim()) ? (
                              <span className="font-mono tabular-nums">••{String(e.bank_account).slice(-4)}</span>
                            ) : (
                              <span data-testid={`deposit-warn-${e.employee_id}`} className="inline-flex items-center gap-1 border border-red-500 bg-red-50 text-red-700 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5">
                                <AlertTriangle className="h-3 w-3" /> No banking
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums">{p ? money(grossOf(p)) : "—"}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-red-600">{p ? money(p.total_deductions) : "—"}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-emerald-600 font-medium">{p ? money(p.net_pay) : "—"}</td>
                          <td className="px-3 py-3 text-right">
                            <button data-testid={`remove-${e.employee_id}`} onClick={() => { setExcluded((x) => [...x, e.employee_id]); toast.success(`${e.first_name} ${e.last_name} removed from this run`); }} title="Remove from this run" className="text-muted-foreground hover:text-red-600 transition-colors">
                              <X className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-black font-bold bg-neutral-50" data-testid="run-totals-row">
                      <td className="px-4 py-3 sticky left-0 bg-neutral-50">TOTAL</td>
                      <td className="px-3 py-3 text-right tabular-nums" data-testid="run-total-hours">{totalHours.toFixed(2)}</td>
                      <td className="px-3 py-3"></td>
                      <td className="px-3 py-3"></td>
                      <td className="px-4 py-3 text-right tabular-nums" data-testid="run-total-gross">{preview ? money(totalGross) : "—"}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-red-600" data-testid="run-total-deductions">{preview ? money(totalDed) : "—"}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-emerald-600" data-testid="run-total-net">{preview ? money(preview.totals.net_pay) : "—"}</td>
                      <td className="px-3 py-3"></td>
                    </tr>
                  </tfoot>
                </table>
                <div className="px-5 py-3 border-t border-border text-[10px] text-muted-foreground">Direct Deposit employees are included in the EFT file; Manual ones appear on the cheque list. Salaried pay is fixed; hours feed ROE. Totals update live from the CRA payroll engine.</div>
              </div>
            ) : (
              <div className="px-5 py-16 text-center text-sm text-muted-foreground flex flex-col items-center gap-3">
                <PlayCircle className="h-10 w-10 text-muted-foreground" />
                No active employees for this company.
              </div>
            )}
          </div>
          {preview && (
            <div className="grid grid-cols-3 gap-3 mt-4">
              <TotalCard label="Gross" value={money(totalGross)} testid="summary-gross" />
              <TotalCard label="Deductions" value={money(totalDed)} accent="text-red-600" testid="summary-deductions" />
              <TotalCard label="Net Pay" value={money(preview.totals.net_pay)} accent="text-emerald-600" testid="summary-net" />
            </div>
          )}
          {preview && (
            <div data-testid="employer-cost-summary" className="border border-border bg-white mt-4">
              <div className="px-5 py-3 border-b border-border text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Employer Cost (this run)</div>
              <div className="px-5 py-3 space-y-1.5 text-sm">
                <EmpCostRow label="Gross payroll" value={empCost.gross} />
                <EmpCostRow label="Employer CPP" value={empCost.cpp} />
                <EmpCostRow label="Employer CPP2" value={empCost.cpp2} />
                <EmpCostRow label="Employer EI" value={empCost.ei} />
                <EmpCostRow label="Employer match" value={empCost.match} />
                <div className="flex justify-between pt-2 mt-1 border-t border-black font-bold">
                  <span>Total employer cost</span>
                  <span className="tabular-nums" data-testid="employer-cost-total">{money(empCost.total)}</span>
                </div>
              </div>
              <p className="px-5 pb-3 text-[10px] text-muted-foreground">Employee deductions are not employer cost. Values come from the CRA payroll engine.</p>
            </div>
          )}
        </div>
      </div>

      <PayslipDetail slip={detail} companyId={companyId} meta={meta} peopleHref={peopleHref} employee={detail ? employees.find((e) => e.employee_id === detail.employee_id) : null} onApply={applyAdjustment} onHolidayOverride={applyHolidayOverride} otOverride={detail ? (otOverrides[detail.employee_id] ?? "") : ""} onOtOverride={applyOtOverride} otChoice={detail ? (otChoice[detail.employee_id] || "pay") : "pay"} onOtChoice={applyOtChoice} choice={detail ? (holidayChoice[detail.employee_id] || "premium") : "premium"} onHolidayChoice={setHolChoice} vacOverride={detail ? (vacOverrides[detail.employee_id] ?? "") : ""} vacReason={detail ? (vacReasons[detail.employee_id] ?? "") : ""} onVacOverride={setVacOverride} onLineOverride={setLineOverride} onResetLine={resetLine} onClose={() => setDetail(null)} />

      <Dialog open={nudgeOpen} onOpenChange={setNudgeOpen}>
        <DialogContent data-testid="migration-nudge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" /> Migration review incomplete</DialogTitle>
            <DialogDescription>
              {readiness?.counts?.needs_review || 0} active employee{(readiness?.counts?.needs_review || 0) === 1 ? " has" : "s have"} not yet been confirmed as migrated or as new hires. You can continue, but reviewing migration status before the first live payroll is recommended.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap justify-end gap-2 pt-2">
            <button data-testid="nudge-review-migration" onClick={() => { setNudgeOpen(false); reviewMigration(); }} className="border border-border px-4 py-2 text-sm hover:border-black transition-colors">Review Migration</button>
            <button data-testid="nudge-continue-payroll" onClick={continuePayroll} className="bg-black text-white px-4 py-2 text-sm font-medium hover:opacity-90 transition-opacity">Continue Payroll</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TotalCard({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="border border-border bg-white p-5">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`mt-2 text-2xl font-black tracking-tight tabular-nums ${accent || ""}`}>{value}</div>
    </div>
  );
}

function YtdItem({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums font-medium">{money(value)}</span>
    </div>
  );
}

function LineRow({ label, value, negative, bold }) {
  return (
    <div className={`flex justify-between py-1.5 text-sm ${bold ? "font-bold border-t border-black mt-1 pt-2" : "border-b border-border last:border-0"}`}>
      <span className={negative ? "text-red-700" : ""}>{label}</span>
      <span className={`tabular-nums ${negative ? "text-red-700" : ""}`}>{negative ? "-" : ""}{money(value)}</span>
    </div>
  );
}

function EmpCostRow({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums">{money(value)}</span>
    </div>
  );
}

// A coded/legacy deduction line with annual/per-pay cap provenance (backend values only).
function DeductionLine({ d }) {
  const cap = d.annual_cap;
  const capped = d._capped_from != null;
  const reason = d.cap_reason;
  const both = Array.isArray(d.cap_reasons) && d.cap_reasons.length > 1;
  const reached = cap && Number(cap.remaining_before_current) === 0 && reason === "ANNUAL_MAX";
  const label = `${d.code ? d.code + " — " : ""}${d.name || (d.code ? "" : "Deduction")}${d.pre_tax ? " (pre-tax)" : ""}${d.requires_treatment_review ? " · Review" : ""}${!d.code ? " · Legacy" : ""}`;
  return (
    <div className="border-b border-border last:border-0 py-1.5" data-testid={`ded-line-${d.code || d.name}`}>
      <div className="flex justify-between text-sm">
        <span className="text-red-700 flex items-center gap-1.5">
          {label}
          {d.requires_treatment_review && (
            <span title="Treatment may depend on CRA circumstances." className="text-[9px] font-mono uppercase border border-amber-500 bg-amber-50 text-amber-700 px-1 py-0.5">review</span>
          )}
        </span>
        <span className="tabular-nums text-red-700">-{money(d.amount)}</span>
      </div>
      {capped && (
        <div className="mt-1 flex flex-wrap gap-1.5" data-testid={`cap-badge-${d.code || d.name}`}>
          {reached ? (
            <span className="text-[9px] font-mono uppercase border border-red-600 bg-red-50 text-red-700 px-1.5 py-0.5">Annual payroll deduction maximum reached</span>
          ) : both ? (
            <span className="text-[9px] font-mono uppercase border border-orange-500 bg-orange-50 text-orange-700 px-1.5 py-0.5">Per-pay and annual limits evaluated — {reason === "ANNUAL_MAX" ? "annual" : "per-pay"} limit applied</span>
          ) : reason === "ANNUAL_MAX" ? (
            <span className="text-[9px] font-mono uppercase border border-orange-500 bg-orange-50 text-orange-700 px-1.5 py-0.5">Annual maximum applied</span>
          ) : reason === "PER_PAY_MAX" ? (
            <span className="text-[9px] font-mono uppercase border border-blue-500 bg-blue-50 text-blue-700 px-1.5 py-0.5">Per-pay maximum applied</span>
          ) : null}
        </div>
      )}
      {cap && (
        <div className="mt-1 pl-3 text-[11px] text-muted-foreground space-y-0.5" data-testid={`cap-detail-${d.code || d.name}`}>
          <div className="flex justify-between"><span>Requested</span><span className="tabular-nums">{money(cap.requested_amount)}</span></div>
          <div className="flex justify-between"><span>Applied</span><span className="tabular-nums">{money(cap.applied_amount)}</span></div>
          <div className="flex justify-between"><span>Annual code limit</span><span className="tabular-nums">{money(cap.annual_max)}</span></div>
          <div className="flex justify-between"><span>Prior YTD</span><span className="tabular-nums">{money(cap.combined_prior_ytd)}</span></div>
          <div className="flex justify-between"><span>Remaining payroll deduction limit (before this pay)</span><span className="tabular-nums">{money(cap.remaining_before_current)}</span></div>
          {cap.per_pay_max != null && <div className="flex justify-between"><span>Per-pay maximum</span><span className="tabular-nums">{money(cap.per_pay_max)}</span></div>}
        </div>
      )}
      {d.annual_warning && (
        <div className="mt-1 border border-orange-400 bg-orange-50 px-2 py-1 text-[11px] text-orange-800" data-testid={`prior-warn-${d.code || d.name}`}>
          🟠 {d.annual_warning.warning_message} — prior-system deductions may need to be entered before the remaining limit can be confirmed.
        </div>
      )}
    </div>
  );
}

function OvertimePanel({ slip, otVal, setOtVal, onOtOverride, otChoice, onOtChoice }) {
  const ob = slip.overtime_breakdown;
  const label = { weekly: "ESA weekly test (44 h per work week)", override: "Manual override", averaging: "Averaging agreement on file — period total ÷ 44 h/week", period: "Period total test (no timesheet hours)", exempt: "Overtime-exempt role — no weekly test" }[ob.method] || ob.method;
  return (
    <div data-testid="overtime-panel" className="min-w-0 sm:col-span-2 border-t border-border pt-3">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Overtime — ESA {ob.method === "override" && <span className="ml-1 border border-amber-500 bg-amber-50 text-amber-700 px-1.5 py-0.5">override</span>}</div>
      <div className="text-xs text-muted-foreground mb-2">{label}{ob.note ? ` · ${ob.note}` : ""}</div>
      {ob.weeks?.length > 0 && (
        <table className="w-full text-xs mb-2" data-testid="overtime-weeks-table">
          <thead><tr className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground"><th className="text-left py-1">Work week</th><th className="text-right py-1">Worked</th><th className="text-right py-1">OT-eligible</th><th className="text-right py-1">Coded OT</th><th className="text-right py-1">Auto OT</th></tr></thead>
          <tbody>{ob.weeks.map((w) => (
            <tr key={w.week_start} className="border-t border-border/60 tabular-nums">
              <td className="py-1">{w.week_start} → {w.week_end}{w.deferred && <span className="ml-1 text-[9px] text-muted-foreground">(ends next period — tested then)</span>}</td>
              <td className="text-right">{w.worked}h</td><td className="text-right">{w.eligible}h</td><td className="text-right">{w.manual_ot}h</td>
              <td className={`text-right font-semibold ${w.auto_ot > 0 ? "text-amber-700" : ""}`}>{w.auto_ot}h</td>
            </tr>))}</tbody>
        </table>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs">OT hours this run: <strong className="tabular-nums" data-testid="overtime-hours-value">{slip.overtime_hours ?? "auto"}</strong>{ob.lieu ? <> × 1.5 = <strong className="tabular-nums text-emerald-700" data-testid="overtime-lieu-banked">{ob.lieu_hours_banked}h banked</strong></> : <> × 1.5 = <strong className="tabular-nums">{money(slip.overtime_pay || 0)}</strong></>}</span>
        {ob.method !== "exempt" && (
          <select data-testid="overtime-choice-select" value={otChoice || "pay"} onChange={(e) => onOtChoice(slip.employee_id, e.target.value)} className="border border-input bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black">
            <option value="pay">Pay overtime at 1.5×</option>
            <option value="lieu">Bank as time in lieu (1.5 h per OT hour)</option>
          </select>
        )}
        <input data-testid="overtime-override-input" type="number" step="0.25" min="0" placeholder="OT hours" value={otVal} onChange={(e) => setOtVal(e.target.value)} className="w-24 border border-input px-2 py-1.5 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-black" />
        <button data-testid="overtime-override-apply" onClick={() => onOtOverride(slip.employee_id, otVal)} className="border border-black bg-white px-3 py-1.5 text-sm font-medium hover:bg-black hover:text-white transition-colors">Set OT hours</button>
        {ob.method === "override" && <button data-testid="overtime-override-clear" onClick={() => { setOtVal(""); onOtOverride(slip.employee_id, ""); }} className="border border-border px-3 py-1.5 text-sm hover:border-red-600 hover:text-red-600 transition-colors">Back to auto</button>}
      </div>
      <p className="text-[10px] text-muted-foreground mt-1">Overrides the ESA-calculated overtime hours for this run only; regular hours are reduced by the same amount.</p>
    </div>
  );
}

function PayslipDetail({ slip, companyId, meta, peopleHref, employee, onApply, onHolidayOverride, otOverride, onOtOverride, otChoice, onOtChoice, choice, onHolidayChoice, vacOverride, vacReason, onVacOverride, onLineOverride, onResetLine, onClose }) {
  const [oneOff, setOneOff] = useState({ kind: "earning", name: "", amount: "", taxable: true, vacationable: true });
  const [holVal, setHolVal] = useState("");
  const [otVal, setOtVal] = useState(otOverride ?? "");
  useEffect(() => { setOtVal(otOverride ?? ""); }, [otOverride, slip?.employee_id]);
  const [vacVal, setVacVal] = useState("");
  const [vacNote, setVacNote] = useState("");
  const [edEarn, setEdEarn] = useState([]);
  const [edDed, setEdDed] = useState([]);
  const [edStat, setEdStat] = useState({});
  const [edStatOrig, setEdStatOrig] = useState({});
  const [codes, setCodes] = useState({ earning: [], deduction: [] });
  const [resolved, setResolved] = useState({ lines: [], review: [] });
  const [picker, setPicker] = useState({ kind: "deduction", code_id: "", amount: "", rate: "" });
  const [auto, setAuto] = useState({ cost_of_automobile: "", total_km: "", personal_km: "", days_available: "", reimbursements: "" });
  const [autoResult, setAutoResult] = useState(null);
  const [autoErr, setAutoErr] = useState(null);
  useEffect(() => {
    setHolVal(slip ? (slip.public_holiday_pay ?? 0) : ""); setVacVal(vacOverride ?? ""); setVacNote(vacReason ?? "");
    setEdEarn(slip ? (slip.custom_earnings || []).map((e) => ({ ...e })) : []);
    setEdDed(slip ? (slip.custom_deductions || []).map((d) => ({ ...d })) : []);
    setEdStat(slip ? { federal_tax: slip.federal_tax, provincial_tax: slip.provincial_tax, cpp: slip.cpp, ei: slip.ei } : {});
    setEdStatOrig(slip ? { federal_tax: slip.federal_tax, provincial_tax: slip.provincial_tax, cpp: slip.cpp, ei: slip.ei } : {});
  }, [slip, vacOverride, vacReason]);
  useEffect(() => {
    if (!slip || !companyId) return;
    const pd = meta?.pay_date || "";
    Promise.all([
      api.get(`/companies/${companyId}/earning-codes`),
      api.get(`/companies/${companyId}/deduction-codes`),
      api.get(`/employees/${slip.employee_id}/resolved-lines?pay_date=${pd}`),
    ]).then(([e, d, r]) => {
      setCodes({ earning: e.data.codes || [], deduction: d.data.codes || [] });
      setResolved(r.data || { lines: [], review: [] });
    }).catch(() => {});
  }, [slip, companyId, meta]);
  if (!slip) return null;
  const earnings = slip.custom_earnings || [];
  // REG — Regular Pay lines are the same concept as Base earnings → shown merged into that line.
  const regLines = earnings.filter((e) => (e.code || "").toUpperCase() === "REG");
  const otherEarnings = earnings.filter((e) => (e.code || "").toUpperCase() !== "REG");
  const deductions = slip.custom_deductions || [];
  const payDate = meta?.pay_date || "";
  const effOn = (c) => {
    if (c.active === false) return false;
    if (c.effective_from && payDate < String(c.effective_from).slice(0, 10)) return false;
    if (c.effective_to && payDate > String(c.effective_to).slice(0, 10)) return false;
    return true;
  };
  const availableCodes = (picker.kind === "earning" ? codes.earning : codes.deduction).filter(effOn);
  const applyCoded = () => {
    const list = picker.kind === "earning" ? codes.earning : codes.deduction;
    const c = list.find((x) => x.code_id === picker.code_id);
    if (!c || picker.amount === "" || picker.amount == null) return;
    const amt = Number(picker.amount);
    const line = picker.kind === "earning"
      ? { code: c.code, name: c.name, amount: amt, taxable: c.taxable !== false,
          cpp_pensionable: c.cpp_pensionable, cpp2_applicable: c.cpp2_applicable,
          ei_insurable: c.ei_insurable, vacationable: c.vacationable,
          non_cash: !!c.non_cash, benefit_category: c.benefit_category, employer_paid: !!c.employer_paid,
          gl_expense_account: c.gl_expense_account,
          requires_treatment_review: !!c.requires_treatment_review }
      : { code: c.code, name: c.name, amount: amt, pre_tax: !!c.pre_tax,
          reduces_taxable_income: !!c.reduces_taxable_income,
          reduces_cpp_pensionable_earnings: !!c.reduces_cpp_pensionable_earnings,
          reduces_ei_insurable_earnings: !!c.reduces_ei_insurable_earnings,
          priority: c.priority ?? 100, per_pay_max: c.per_pay_max ?? null, annual_max: c.annual_max ?? null,
          employer_match_enabled: !!c.employer_match_enabled, employer_match_type: c.employer_match_type,
          employer_match_rate: c.employer_match_rate, employer_match_max: c.employer_match_max,
          requires_treatment_review: !!c.requires_treatment_review };
    onApply(slip.employee_id, line, picker.kind);
    setPicker({ kind: picker.kind, code_id: "", amount: "", rate: "" });
  };

  const calcAuto = async () => {
    setAutoErr(null); setAutoResult(null);
    try {
      const { data } = await api.post("/payroll/automobile-benefit/calculate", {
        company_id: companyId, tax_year: payDate ? Number(String(payDate).slice(0, 4)) : undefined,
        cost_of_automobile: Number(auto.cost_of_automobile || 0), total_km: Number(auto.total_km || 0),
        personal_km: Number(auto.personal_km || 0), days_available: Number(auto.days_available || 0),
        reimbursements: Number(auto.reimbursements || 0) });
      setAutoResult(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setAutoErr(d?.errors ? d.errors.join(" ") : (d?.message || "Could not calculate automobile benefit."));
    }
  };
  const addAutoLine = () => {
    if (!autoResult) return;
    const ec = codes.earning.find((x) => x.code === "AUTOBEN");
    onApply(slip.employee_id, {
      code: "AUTOBEN", name: "Automobile Benefit", amount: autoResult.taxable_benefit,
      taxable: true, non_cash: true, ei_insurable: ec?.ei_insurable ?? false,
      cpp_pensionable: ec?.cpp_pensionable ?? true, benefit_category: "AUTOMOBILE",
      gl_expense_account: ec?.gl_expense_account || null, requires_treatment_review: true,
      automobile: autoResult.provenance }, "earning");
    setAutoResult(null); setAuto({ cost_of_automobile: "", total_km: "", personal_km: "", days_available: "", reimbursements: "" });
  };

  const downloadStub = async () => {
    try {
      const res = await api.post("/payroll/paystub-preview",
        { company_id: companyId, employee_id: slip.employee_id, payslip: slip, meta },
        { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch { toast.error("Could not generate stub"); }
  };

  const apply = () => {
    if (!oneOff.name || !oneOff.amount) return;
    const line = oneOff.kind === "earning"
      ? { name: oneOff.name, amount: Number(oneOff.amount), taxable: oneOff.taxable !== false, vacationable: oneOff.vacationable !== false }
      : { name: oneOff.name, amount: Number(oneOff.amount), pre_tax: false };
    onApply(slip.employee_id, line, oneOff.kind);
    setOneOff({ kind: "earning", name: "", amount: "", taxable: true, vacationable: true });
  };
  return (
    <Dialog open={!!slip} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none border-black w-[96vw] sm:max-w-[1200px] max-h-[94vh] p-0 gap-0 flex flex-col overflow-hidden" data-testid="payslip-detail-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-border">
          <DialogTitle className="tracking-tight">{slip.employee_name} — Payslip Preview</DialogTitle>
          <DialogDescription className="text-xs font-mono">
            {slip.hours != null ? `${slip.hours} hrs · ` : ""}{FREQ_LABELS[slip.pay_frequency] || ""}
            {slip.public_holidays?.length ? ` · Holidays: ${slip.public_holidays.join(", ")}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-4 pb-6 space-y-4" data-testid="payslip-detail-body">
        <div className="bg-black text-white px-4 py-3 flex justify-between items-center" data-testid="payslip-detail-net-top">
          <span className="font-bold">NET PAY</span>
          <span className="tabular-nums font-bold text-lg">{money(slip.net_pay)}</span>
        </div>
        <div className="grid sm:grid-cols-2 gap-6 py-2">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Earnings</div>
            <LineRow label="Base earnings" value={(slip.base_earnings || 0) + regLines.reduce((t, e) => t + (Number(e.amount) || 0), 0)} />
            {regLines.length > 0 && (
              <div data-testid="base-earnings-reg-merged" className="pl-3 -mt-1 mb-1 text-[11px] text-muted-foreground">
                {regLines.map((e, i) => <div key={i}>REG — Regular Pay {e.hours ? `(${e.hours}h)` : ""} {money(e.amount)}{e._source === "time_module" ? " · from approved time" : ""}</div>)}
              </div>
            )}
            {(slip.rate_breakdown || []).length > 0 && (
              <div data-testid="rate-breakdown" className="pl-3 -mt-1 mb-1 text-[11px] text-muted-foreground">
                {slip.rate_breakdown.map((b, i) => (
                  <div key={i}>{b.hours}h @ ${Number(b.rate).toFixed(2)}{i < slip.rate_breakdown.length - 1 ? "  +" : ""}</div>
                ))}
                <div className="italic">Minimum-wage change during this pay period</div>
              </div>
            )}
            {(slip.overtime_pay || 0) > 0 && <LineRow label="Overtime (1.5×)" value={slip.overtime_pay} />}
            {(slip.public_holiday_pay || 0) > 0 && <LineRow label="Public holiday pay" value={slip.public_holiday_pay} />}
            {otherEarnings.map((e, i) => (
              <LineRow key={i} label={`${e.code ? e.code + " — " : ""}${e.name || (e.code ? "" : "Earning")}${e.taxable === false ? " (non-tax)" : ""}${e.requires_treatment_review ? " · Review" : ""}${!e.code ? " · Legacy" : ""}`} value={e.amount} />
            ))}
            {(slip.vacation_pay || 0) > 0 && <LineRow label={`Vacation pay (${slip.effective_vacation_rate}%)`} value={slip.vacation_pay} />}
            {(slip.vacation_paid_from_bank || 0) > 0 && <LineRow label="Vacation payout (banked)" value={slip.vacation_paid_from_bank} />}
            <LineRow label="Gross pay" value={(slip.gross || 0) + (slip.nontaxable_income || 0)} bold />
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Deductions</div>
            <LineRow label="Federal tax" value={slip.federal_tax} negative />
            <LineRow label="Ontario tax" value={slip.provincial_tax} negative />
            <LineRow label="CPP" value={slip.cpp} negative />
            {(slip.cpp2 || 0) > 0 && <LineRow label="CPP2" value={slip.cpp2} negative />}
            <LineRow label="EI" value={slip.ei} negative />
            {deductions.map((d, i) => (
              <DeductionLine key={i} d={d} />
            ))}
            <LineRow label="Total deductions" value={slip.total_deductions} negative bold />
          </div>
        </div>
        {slip.negative_net && (
          <div data-testid="detail-negative-net" className="border-2 border-red-600 bg-red-50 px-4 py-3">
            <div className="flex items-center gap-2 mb-1"><AlertTriangle className="h-4 w-4 text-red-700" /><span className="font-bold text-red-800">Negative Net Pay</span></div>
            <div className="font-mono text-xs text-red-900">{slip.negative_net.code && <>{slip.negative_net.code} — {money(slip.negative_net.deduction_amount)} · Net before deduction: {money(slip.negative_net.net_before_deduction)} · </>}Resulting net: <span className="font-bold">{money(slip.negative_net.resulting_net)}</span></div>
            <p className="text-xs text-red-800 mt-1 font-medium">This payroll cannot be posted until corrected.</p>
          </div>
        )}
        {(slip.deduction_annual_warnings || []).length > 0 && (
          <div data-testid="detail-prior-system-warning" className="border border-orange-400 bg-orange-50 px-4 py-3">
            <div className="flex items-center gap-2 mb-1"><AlertTriangle className="h-4 w-4 text-orange-600" /><span className="font-bold text-orange-800">Prior-system deduction YTD may be required</span></div>
            <p className="text-xs text-orange-800">This employee was migrated into PostWage and this deduction has an annual payroll limit. Prior-system deductions may need to be entered before the remaining limit can be confirmed.</p>
            {peopleHref && <a data-testid="detail-goto-opening-ytd" href={peopleHref} className="inline-flex items-center gap-1 mt-2 text-xs font-medium border border-orange-500 text-orange-800 px-2.5 py-1 hover:bg-orange-500 hover:text-white transition-colors">Enter Opening Deduction YTD →</a>}
          </div>
        )}
        <div data-testid="recurring-lines" className="border border-border">
          <div className="px-4 py-2 border-b border-border text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Recurring coded lines (from assignments)</div>
          <div className="px-4 py-2 text-sm">
            {resolved.lines.length === 0 && resolved.review.length === 0 && (
              <p className="text-xs text-muted-foreground">No recurring coded earning/deduction assignments for this pay date.</p>
            )}
            {resolved.lines.map((l, i) => {
              const s = l.snapshot || {};
              const op = s.operational || {};
              return (
                <div key={`rl${i}`} data-testid={`recurring-line-${s.code}`} className="flex items-center justify-between py-1 border-b border-border last:border-0">
                  <span className="flex items-center gap-1.5">
                    <span className="text-[9px] font-mono uppercase text-muted-foreground w-10">{l.kind === "earning" ? "earn" : "ded"}</span>
                    <span>{s.code} — {s.code_name}</span>
                    {s.requires_treatment_review && <span title="Treatment may depend on CRA circumstances." className="text-[9px] font-mono uppercase border border-amber-500 bg-amber-50 text-amber-700 px-1 py-0.5">review</span>}
                  </span>
                  <span className="tabular-nums text-xs text-muted-foreground">{op.amount != null ? money(op.amount) : op.rate != null ? `${op.rate}/hr` : op.percentage != null ? `${op.percentage}%` : "—"} · assignment</span>
                </div>
              );
            })}
            {resolved.review.map((r, i) => (
              <div key={`rv${i}`} data-testid={`recurring-review-${r.code || r.code_id}`} className="flex items-center justify-between py-1 border-b border-border last:border-0 text-orange-800">
                <span className="flex items-center gap-1.5"><AlertTriangle className="h-3 w-3 text-orange-600" /> {r.code || r.code_id} ({r.kind})</span>
                <span className="text-xs">{r.reason}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="border-t border-border pt-3 mt-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Add coded line (this run only)</div>
          <div className="flex flex-wrap items-center gap-2">
            <select data-testid="coded-kind" value={picker.kind} onChange={(e) => setPicker({ kind: e.target.value, code_id: "", amount: "", rate: "" })} className="border border-input px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black">
              <option value="earning">Earning code</option>
              <option value="deduction">Deduction code</option>
            </select>
            <select data-testid="coded-code" value={picker.code_id} onChange={(e) => setPicker({ ...picker, code_id: e.target.value })} className="flex-1 min-w-48 border border-input bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black">
              <option value="">Select a code…</option>
              {availableCodes.map((c) => (
                <option key={c.code_id} value={c.code_id}>{c.code} — {c.name}{c.requires_treatment_review ? " · Review" : ""}</option>
              ))}
            </select>
            <input data-testid="coded-amount" placeholder="Amount" type="number" value={picker.amount} onChange={(e) => setPicker({ ...picker, amount: e.target.value })} className="w-24 border border-input px-2 py-1.5 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-black" />
            <button data-testid="coded-apply" onClick={applyCoded} disabled={!picker.code_id || picker.amount === ""} className="border border-black bg-white px-3 py-1.5 text-sm font-medium hover:bg-black hover:text-white transition-colors disabled:opacity-50">Add code</button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">Only active codes effective for this pay date are listed. Statutory treatment (tax/CPP/CPP2/EI/vacation/ROE/T4) comes from the code and is applied by the backend — only the operational amount is entered here.</p>
        </div>
        <div className="border-t border-border pt-3 mt-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Add one-off line (this run only)</div>
          <div className="flex flex-wrap items-center gap-2">
            <select data-testid="oneoff-kind" value={oneOff.kind} onChange={(e) => setOneOff({ ...oneOff, kind: e.target.value })} className="border border-input px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black">
              <option value="earning">Earning (bonus)</option>
              <option value="deduction">Deduction</option>
            </select>
            <input data-testid="oneoff-name" placeholder="Name" value={oneOff.name} onChange={(e) => setOneOff({ ...oneOff, name: e.target.value })} className="flex-1 min-w-24 border border-input px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
            <input data-testid="oneoff-amount" placeholder="Amt" type="number" value={oneOff.amount} onChange={(e) => setOneOff({ ...oneOff, amount: e.target.value })} className="w-24 border border-input px-2 py-1.5 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-black" />
            {oneOff.kind === "earning" && (
              <>
                <label className="flex items-center gap-1 text-[11px] whitespace-nowrap" title="Taxable earning"><input type="checkbox" data-testid="oneoff-taxable" checked={oneOff.taxable !== false} onChange={(e) => setOneOff({ ...oneOff, taxable: e.target.checked })} className="accent-black" /> tax</label>
                <label className="flex items-center gap-1 text-[11px] whitespace-nowrap" title="Include in vacation pay"><input type="checkbox" data-testid="oneoff-vacpay" checked={oneOff.vacationable !== false} onChange={(e) => setOneOff({ ...oneOff, vacationable: e.target.checked })} className="accent-black" /> vac</label>
              </>
            )}
            <button data-testid="oneoff-apply" onClick={apply} className="border border-black bg-white px-3 py-1.5 text-sm font-medium hover:bg-black hover:text-white transition-colors">Apply</button>
          </div>
        </div>
        {(slip.vacation_accrued || 0) > 0 && (
          <p className="text-xs text-muted-foreground">Vacation accrued this period: <span className="tabular-nums text-emerald-700">{money(slip.vacation_accrued)}</span> (banked)</p>
        )}
        <div className="border-t border-border pt-3 mt-1 grid sm:grid-cols-2 gap-4" data-testid="run-overrides-row">
          <div data-testid="vac-override-panel" className="min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Vacation salary override (this run)</div>
          <div className="flex flex-wrap items-center gap-2">
            <input data-testid="vac-override-input" type="number" step="0.01" placeholder={slip.vacation_mode === "payout" ? "vacation pay $" : "accrual $"} value={vacVal} onChange={(e) => setVacVal(e.target.value)} className="w-28 border border-input px-2 py-1.5 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-black" />
            <input data-testid="vac-override-reason" placeholder="Reason (audit)" value={vacNote} onChange={(e) => setVacNote(e.target.value)} className="flex-1 min-w-24 border border-input px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
            <button data-testid="vac-override-apply" onClick={() => onVacOverride(slip.employee_id, vacVal, vacNote)} className="border border-black bg-white px-3 py-1.5 text-sm font-medium hover:bg-black hover:text-white transition-colors">Apply</button>
            <button data-testid="vac-override-clear" onClick={() => { setVacVal(""); onVacOverride(slip.employee_id, "", vacNote); }} className="border border-border px-3 py-1.5 text-sm hover:border-red-600 hover:text-red-600 transition-colors">Clear</button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">Reduce or zero this salaried employee's vacation {slip.vacation_mode === "payout" ? "pay" : "accrual"} for this run only. Set 0 to skip vacation entirely.</p>
        </div>
          <div data-testid="holiday-override-panel" className="min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Statutory holiday pay override</div>
          <div className="flex flex-wrap items-center gap-2">
            <input data-testid="holiday-override-input" type="number" value={holVal} onChange={(e) => setHolVal(e.target.value)} className="w-24 border border-input px-2 py-1.5 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-black" />
            <button data-testid="holiday-override-apply" onClick={() => onHolidayOverride(slip.employee_id, holVal)} className="border border-black bg-white px-3 py-1.5 text-sm font-medium hover:bg-black hover:text-white transition-colors">Set holiday pay</button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">Overrides the ESA-calculated amount for this run only.</p>
          {(slip.holiday_worked_hours || 0) > 0 && (
            <div className="mt-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Worked the holiday ({slip.holiday_worked_hours}h) — ESA option</div>
              <select data-testid="holiday-choice-select" value={choice || "premium"} onChange={(e) => onHolidayChoice(slip.employee_id, e.target.value)} className="w-full border border-input bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black">
                <option value="premium">Premium pay 1.5× for hours worked (+ public holiday pay)</option>
                <option value="substitute">Substitute day off (regular pay now + banked day)</option>
              </select>
              <p className="text-[10px] text-muted-foreground mt-1">{choice === "substitute" ? "Holiday hours paid at regular rate; a substitute day off is banked (public-holiday pay deferred to when taken)." : "Holiday hours paid at 1.5× premium, plus the ÷20 public-holiday pay this period."}</p>
            </div>
          )}
        </div>
          {slip.overtime_breakdown && <OvertimePanel slip={slip} otVal={otVal} setOtVal={setOtVal} onOtOverride={onOtOverride} otChoice={otChoice} onOtChoice={onOtChoice} />}
        </div>
        <div className="border-t border-border pt-3 mt-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Edit / delete earnings &amp; deductions (this run)</div>
          <div className="space-y-1.5">
            {edEarn.map((e, i) => (
              <div key={`e${i}`} className="flex items-center gap-2" data-testid={`edit-earn-${i}`}>
                <span className="text-[10px] font-mono uppercase text-emerald-700 w-10">earn</span>
                <input value={e.name} onChange={(ev) => setEdEarn(edEarn.map((x, j) => j === i ? { ...x, name: ev.target.value } : x))} className="flex-1 border border-input px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
                <input data-testid={`edit-earn-amount-${i}`} type="number" value={e.amount} onChange={(ev) => setEdEarn(edEarn.map((x, j) => j === i ? { ...x, amount: Number(ev.target.value) } : x))} className="w-24 border border-input px-2 py-1 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-black" />
                <label className="flex items-center gap-1 text-[11px] whitespace-nowrap" title="Taxable earning"><input type="checkbox" data-testid={`edit-earn-taxable-${i}`} checked={e.taxable !== false} onChange={(ev) => setEdEarn(edEarn.map((x, j) => j === i ? { ...x, taxable: ev.target.checked } : x))} className="accent-black" /> tax</label>
                <label className="flex items-center gap-1 text-[11px] whitespace-nowrap" title="Include in vacation pay"><input type="checkbox" data-testid={`edit-earn-vacpay-${i}`} checked={e.vacationable !== undefined ? e.vacationable : (e.taxable !== false)} onChange={(ev) => setEdEarn(edEarn.map((x, j) => j === i ? { ...x, vacationable: ev.target.checked } : x))} className="accent-black" /> vac</label>
                <button data-testid={`delete-earn-${i}`} onClick={() => setEdEarn(edEarn.filter((_, j) => j !== i))} className="h-7 w-7 flex items-center justify-center border border-border hover:border-red-600 hover:bg-red-600 hover:text-white transition-colors">×</button>
              </div>
            ))}
            {edDed.map((d, i) => (
              <div key={`d${i}`} className="flex items-center gap-2" data-testid={`edit-ded-${i}`}>
                <span className="text-[10px] font-mono uppercase text-red-700 w-10">ded</span>
                <input value={d.name} onChange={(ev) => setEdDed(edDed.map((x, j) => j === i ? { ...x, name: ev.target.value } : x))} className="flex-1 border border-input px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
                <input data-testid={`edit-ded-amount-${i}`} type="number" value={d.amount} onChange={(ev) => setEdDed(edDed.map((x, j) => j === i ? { ...x, amount: Number(ev.target.value) } : x))} className="w-24 border border-input px-2 py-1 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-black" />
                <button data-testid={`delete-ded-${i}`} onClick={() => setEdDed(edDed.filter((_, j) => j !== i))} className="h-7 w-7 flex items-center justify-center border border-border hover:border-red-600 hover:bg-red-600 hover:text-white transition-colors">×</button>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
            {[["federal_tax", "Federal tax"], ["provincial_tax", "Ontario tax"], ["cpp", "CPP"], ["ei", "EI"]].map(([k, lbl]) => (
              <div key={k}>
                <label className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">{lbl}</label>
                <input data-testid={`edit-stat-${k}`} type="number" value={edStat[k] ?? ""} onChange={(ev) => setEdStat({ ...edStat, [k]: ev.target.value === "" ? "" : Number(ev.target.value) })} className="mt-1 w-full border border-input px-2 py-1 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-black" />
              </div>
            ))}
          </div>
          <button data-testid="apply-line-overrides" onClick={() => {
            const statutory = {};
            Object.keys(edStat).forEach((k) => { if (String(edStat[k]) !== String(edStatOrig[k])) statutory[k] = edStat[k]; });
            onLineOverride(slip.employee_id, { earnings: edEarn, deductions: edDed, statutory });
          }} className="mt-3 w-full border border-black bg-white py-2.5 text-sm font-medium hover:bg-black hover:text-white transition-colors">Apply earnings / deduction changes</button>
          <button data-testid="reset-to-calculated" onClick={() => onResetLine(slip.employee_id)} className="mt-2 w-full border border-border py-2.5 text-sm font-medium hover:border-black transition-colors">↺ Revert to CRA-calculated</button>
          <p className="text-[10px] text-muted-foreground mt-1">Edit amounts, delete lines, or override the statutory tax/CPP/EI amounts for this run only.</p>
        </div>
        {employee?.automobile_benefit_enabled && (
        <div className="border-t border-border pt-3 mt-1" data-testid="automobile-benefit-panel">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Automobile benefit calculator (employer-owned)</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[["cost_of_automobile", "Cost of automobile"], ["total_km", "Total km"], ["personal_km", "Personal km"], ["days_available", "Days available"], ["reimbursements", "Reimbursements"]].map(([k, lbl]) => (
              <div key={k}>
                <label className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">{lbl}</label>
                <input data-testid={`auto-${k}`} type="number" value={auto[k]} onChange={(e) => setAuto({ ...auto, [k]: e.target.value })} className="mt-1 w-full border border-input px-2 py-1 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-black" />
              </div>
            ))}
          </div>
          <button data-testid="auto-calculate" onClick={calcAuto} className="mt-2 w-full border border-black bg-white py-2 text-sm font-medium hover:bg-black hover:text-white transition-colors">Calculate taxable benefit</button>
          {autoErr && <p data-testid="auto-error" className="text-[11px] text-red-600 mt-1">{autoErr}</p>}
          {autoResult && (
            <div data-testid="auto-result" className="mt-2 border border-violet-300 bg-violet-50 p-2 text-xs space-y-0.5">
              <div className="flex justify-between"><span>Standby charge ({autoResult.provenance.standby_method})</span><span className="tabular-nums">{money(autoResult.standby_charge)}</span></div>
              <div className="flex justify-between"><span>Operating-cost benefit</span><span className="tabular-nums">{money(autoResult.operating_cost_benefit)}</span></div>
              <div className="flex justify-between"><span>Less reimbursements</span><span className="tabular-nums">-{money(autoResult.reimbursements)}</span></div>
              <div className="flex justify-between font-semibold border-t border-violet-300 pt-0.5"><span>Taxable automobile benefit ({autoResult.provenance.tax_year})</span><span className="tabular-nums">{money(autoResult.taxable_benefit)}</span></div>
              <button data-testid="auto-add-line" onClick={addAutoLine} className="mt-1 w-full border border-black bg-white py-1.5 text-xs font-medium hover:bg-black hover:text-white transition-colors">Add as AUTOBEN line (non-cash)</button>
            </div>
          )}
          <p className="text-[10px] text-muted-foreground mt-1">Non-cash benefit: increases taxable/CPP income and Box 14, but is not paid as cash. Calculation inputs &amp; parameters are frozen with the posted payroll for audit.</p>
        </div>
        )}
        <div className="bg-black text-white px-4 py-3 flex justify-between items-center mt-2">
          <span className="font-bold">NET PAY</span>
          <span className="tabular-nums font-bold text-lg" data-testid="payslip-detail-net">{money(slip.net_pay)}</span>
        </div>
        <div data-testid="detail-employer-cost" className="border border-border mt-2">
          <div className="px-4 py-2 border-b border-border text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Employer Cost (this employee)</div>
          <div className="px-4 py-2 space-y-1 text-sm">
            <EmpCostRow label="Gross payroll" value={(slip.gross || 0) + (slip.nontaxable_income || 0)} />
            <EmpCostRow label="Employer CPP" value={slip.employer_cpp || 0} />
            <EmpCostRow label="Employer CPP2" value={slip.employer_cpp2 || 0} />
            <EmpCostRow label="Employer EI" value={slip.employer_ei || 0} />
            <EmpCostRow label="Employer match" value={slip.employer_match_total || 0} />
            <div className="flex justify-between pt-1.5 mt-1 border-t border-black font-bold"><span>Total employer cost</span><span className="tabular-nums" data-testid="detail-employer-cost-total">{money(slip.employer_cost || 0)}</span></div>
          </div>
        </div>
        <details className="border-t border-border pt-3 mt-2 group" data-testid="ytd-section">
          <summary className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2 cursor-pointer list-none flex items-center justify-between hover:text-black"><span>Year to date (incl. this run)</span><span className="group-open:rotate-180 transition-transform">▾</span></summary>
          <div className="grid grid-cols-3 gap-x-4 gap-y-1.5 text-sm">
            <YtdItem label="Gross" value={slip.ytd_gross} />
            <YtdItem label="Income tax" value={slip.ytd_income_tax} />
            <YtdItem label="CPP" value={slip.ytd_cpp} />
            <YtdItem label="CPP2" value={slip.ytd_cpp2} />
            <YtdItem label="EI" value={slip.ytd_ei} />
            <YtdItem label="Net pay" value={slip.ytd_net} />
          </div>
        </details>
        </div>
        <div className="shrink-0 px-6 py-3 bg-white border-t border-border flex flex-wrap gap-2" data-testid="payslip-footer">
          <button data-testid="download-stub-button" onClick={downloadStub} className="flex-1 border border-black bg-white py-2.5 text-sm font-medium hover:bg-black hover:text-white transition-colors">Download stub (PDF)</button>
          <a data-testid="payslip-employee-button" href={`${peopleHref}&employee=${slip.employee_id}&from=run-payroll`} title="Open this employee's file (People) to make changes"
            className="flex-1 inline-flex items-center justify-center gap-1.5 border border-border bg-white py-2.5 text-sm font-medium hover:border-black transition-colors"><UserRound className="h-4 w-4" /> Employee</a>
          <button data-testid="payslip-submit-button" onClick={() => { toast.success("Payslip changes kept for this run"); onClose(); }}
            className="flex-1 border border-black bg-black text-white py-2.5 text-sm font-medium hover:bg-white hover:text-black transition-colors">Submit</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DateField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</label>
      <input data-testid={testid} type="date" value={value} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
    </div>
  );
}
